import { useState, useEffect, useCallback, useRef, Fragment } from "react";
import { toast } from "sonner";
import { api, apiError, fmtDate } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { useAuth } from "../context/AuthContext";
import { BookOpen, Sparkles, Upload, BarChart3, ShieldCheck, Loader2, Download, Search, FileText, CheckCircle2, XCircle, FileDown, AlertTriangle, Award, RefreshCw, Calculator, Plus, X, Pencil, Check, Columns, GitCompare } from "lucide-react";
import { WordDiff } from "../components/WordDiff";

const API = process.env.REACT_APP_BACKEND_URL;
const AI_TOOLS = [
  { key: "redaction", label: "Rédaction assistée", desc: "Protocole, mémoire ou publication" },
  { key: "reformulation", label: "Reformulation assistée", desc: "Reformuler un texte selon un objectif" },
  { key: "humanisation", label: "Humanisation IA", desc: "Rendre un texte plus naturel + score d'humanité Winston" },
  { key: "evaluation", label: "Évaluation & correction", desc: "Relecture méthodologique notée /20" },
  { key: "plagiat", label: "Intégrité / plagiat", desc: "Analyse heuristique + reformulations" },
];
const REFORM_MODES = [
  { v: "style", l: "Amélioration stylistique" },
  { v: "simplification", l: "Simplification" },
  { v: "academique", l: "Reformulation académique" },
  { v: "publication", l: "Reformulation pour publication internationale" },
];
const KIND_TITLES = { protocole: "Protocole de recherche", memoire: "Mémoire de recherche", publication: "Article scientifique" };

export default function RechercheScientifique() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [themes, setThemes] = useState([]);

  useEffect(() => { api.get("/recherche/themes").then(({ data }) => setThemes(data.themes)).catch(() => {}); }, []);

  return (
    <div className="mx-auto max-w-6xl px-4 py-10" data-testid="recherche-page">
      <div className="mb-6">
        <h1 className="font-display text-3xl font-bold text-navy sm:text-4xl">Recherche Scientifique</h1>
        <p className="mt-1 text-base text-slate-500">Bibliothèque, soumission, assistant IA de rédaction, évaluation et intégrité — pour étudiants, médecins et chercheurs.</p>
      </div>
      <Tabs defaultValue="bibliotheque">
        <TabsList className="flex flex-wrap">
          <TabsTrigger value="bibliotheque" data-testid="rs-tab-biblio"><BookOpen size={15} className="mr-1" /> Bibliothèque</TabsTrigger>
          <TabsTrigger value="ia" data-testid="rs-tab-ia"><Sparkles size={15} className="mr-1" /> Assistant IA</TabsTrigger>
          <TabsTrigger value="soumettre" data-testid="rs-tab-submit"><Upload size={15} className="mr-1" /> Soumettre</TabsTrigger>
          <TabsTrigger value="mine" data-testid="rs-tab-mine"><FileText size={15} className="mr-1" /> Mes soumissions</TabsTrigger>
          <TabsTrigger value="analyse" data-testid="rs-tab-analyse"><Calculator size={15} className="mr-1" /> Analyse statistique IA</TabsTrigger>
          <TabsTrigger value="stats" data-testid="rs-tab-stats"><BarChart3 size={15} className="mr-1" /> Statistiques</TabsTrigger>
          {isAdmin && <TabsTrigger value="validation" data-testid="rs-tab-validation"><ShieldCheck size={15} className="mr-1" /> Validation</TabsTrigger>}
        </TabsList>
        <TabsContent value="bibliotheque" className="mt-6"><Library themes={themes} isAdmin={isAdmin} /></TabsContent>
        <TabsContent value="ia" className="mt-6"><AiAssistant /></TabsContent>
        <TabsContent value="soumettre" className="mt-6"><SubmitForm themes={themes} /></TabsContent>
        <TabsContent value="mine" className="mt-6"><MySubmissions /></TabsContent>
        <TabsContent value="analyse" className="mt-6"><AnalyseStatTab /></TabsContent>
        <TabsContent value="stats" className="mt-6"><Stats /></TabsContent>
        {isAdmin && <TabsContent value="validation" className="mt-6"><Validation /></TabsContent>}
      </Tabs>
    </div>
  );
}

function Library({ themes }) {
  const [filters, setFilters] = useState({ q: "", theme: "", year: "", author: "" });
  const [papers, setPapers] = useState(null);
  const load = useCallback(() => {
    const p = new URLSearchParams(filters).toString();
    api.get(`/recherche/papers?${p}`).then(({ data }) => setPapers(data.papers)).catch(() => setPapers([]));
  }, [filters]);
  useEffect(() => { load(); }, [load]);

  const download = async (paper) => {
    try {
      const res = await api.get(`/recherche/papers/${paper.id}/download`, { responseType: "blob" });
      const u = URL.createObjectURL(res.data); const a = document.createElement("a");
      a.href = u; a.download = paper.file_name || "article.pdf"; a.click(); URL.revokeObjectURL(u);
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div>
      <div className="mb-4 grid gap-2 sm:grid-cols-4">
        <div className="relative sm:col-span-2"><Search size={15} className="absolute left-3 top-2.5 text-slate-400" /><Input value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })} placeholder="Rechercher un titre, résumé, mot-clé…" className="pl-9" data-testid="rs-search" /></div>
        <select value={filters.theme} onChange={(e) => setFilters({ ...filters, theme: e.target.value })} data-testid="rs-filter-theme" className="rounded-lg border border-slate-200 px-3 py-2 text-sm text-navy"><option value="">Tous les thèmes</option>{themes.map((t) => <option key={t} value={t}>{t}</option>)}</select>
        <Input value={filters.author} onChange={(e) => setFilters({ ...filters, author: e.target.value })} placeholder="Auteur" data-testid="rs-filter-author" />
      </div>
      {!papers ? <div className="flex justify-center py-12"><Loader2 className="animate-spin text-[#0E7C7B]" /></div> :
        papers.length === 0 ? <p className="py-12 text-center text-sm text-slate-400" data-testid="rs-empty">Aucune publication trouvée.</p> : (
          <div className="grid gap-4 md:grid-cols-2">
            {papers.map((p) => (
              <div key={p.id} className="rounded-2xl border border-slate-200 bg-white p-5" data-testid={`rs-paper-${p.id}`}>
                <span className="rounded-full bg-[#0E7C7B]/10 px-2 py-0.5 text-[11px] font-semibold text-[#0E7C7B]">{p.theme}{p.year ? ` · ${p.year}` : ""}</span>
                <h3 className="mt-2 font-display text-lg font-semibold text-navy">{p.title}</h3>
                <p className="text-xs text-slate-500">{p.authors}</p>
                {p.abstract && <p className="mt-2 line-clamp-3 text-sm text-slate-600">{p.abstract}</p>}
                {p.summary_ai && <details className="mt-2"><summary className="cursor-pointer text-xs font-semibold text-[#0E7C7B]">Résumé IA</summary><p className="mt-1 whitespace-pre-wrap text-xs text-slate-600">{p.summary_ai}</p></details>}
                <Button onClick={() => download(p)} data-testid={`rs-dl-${p.id}`} className="mt-3 rounded-full bg-[#0E7C7B] text-xs text-white hover:bg-[#0b6463]"><Download size={13} className="mr-1" /> Télécharger le PDF</Button>
              </div>
            ))}
          </div>
        )}
    </div>
  );
}

function AiAssistant() {
  const [tool, setTool] = useState("redaction");
  const [form, setForm] = useState({ kind: "protocole", topic: "", discipline: "", details: "", text: "" });
  const [status, setStatus] = useState("idle"); // idle|running|done|error
  const [result, setResult] = useState("");
  const [exporting, setExporting] = useState("");
  const [cert, setCert] = useState(null);
  const [certLoading, setCertLoading] = useState(false);
  const [threshold, setThreshold] = useState(15);
  const [reportLoading, setReportLoading] = useState(false);
  const [attachPlagiat, setAttachPlagiat] = useState(true);
  const [reformMode, setReformMode] = useState("style");
  const [attaching, setAttaching] = useState(false);
  const [reformCert, setReformCert] = useState(null);
  const [reformCertLoading, setReformCertLoading] = useState(false);
  const [reformThreshold, setReformThreshold] = useState(15);
  const [reformAttach, setReformAttach] = useState(true);
  const [redCert, setRedCert] = useState(null);
  const [redCertLoading, setRedCertLoading] = useState(false);
  const [redThreshold, setRedThreshold] = useState(15);
  const [redAttach, setRedAttach] = useState(true);
  const [humanScore, setHumanScore] = useState(null);
  const [humanScoreLoading, setHumanScoreLoading] = useState(false);
  const [attachedList, setAttachedList] = useState([]);
  const [showDiff, setShowDiff] = useState(true);

  const rebuildText = (list) => {
    if (list.length === 0) return "";
    if (list.length === 1) return list[0].text;
    return list.map((it) => `===== ${it.name} =====\n${it.text}`).join("\n\n");
  };

  const processFiles = async (files) => {
    const arr = Array.from(files || []).filter(Boolean);
    if (!arr.length) return;
    setAttaching(true);
    try {
      const results = [];
      for (const file of arr) {
        const fd = new FormData(); fd.append("file", file);
        const { data } = await api.post("/recherche/ai/extract-text", fd, { headers: { "Content-Type": "multipart/form-data" } });
        results.push({ name: data.filename || file.name, size: file.size, text: data.text || "", chars: (data.text || "").length });
      }
      setAttachedList((prev) => {
        const next = [...prev, ...results];
        setForm((f) => ({ ...f, text: rebuildText(next) }));
        return next;
      });
      toast.success(arr.length > 1 ? `${arr.length} fichiers importés` : "Texte importé depuis le fichier");
    } catch (err) { toast.error(apiError(err, "Extraction impossible")); } finally { setAttaching(false); }
  };

  const attachFile = async (e) => {
    await processFiles(e.target.files);
    e.target.value = "";
  };

  const removeAttached = (idx) => setAttachedList((prev) => {
    const next = prev.filter((_, i) => i !== idx);
    setForm((f) => ({ ...f, text: rebuildText(next) }));
    return next;
  });

  const clearAttached = () => { setAttachedList([]); setForm((f) => ({ ...f, text: "" })); };

  const [dragOver, setDragOver] = useState(false);
  const onDrop = async (e) => { e.preventDefault(); setDragOver(false); await processFiles(e.dataTransfer?.files); };
  const onDragOver = (e) => { e.preventDefault(); setDragOver(true); };
  const onDragLeave = (e) => { e.preventDefault(); setDragOver(false); };
  const dropProps = { onDrop, onDragOver, onDragLeave };
  const dropCls = dragOver ? "border-[#0E7C7B] ring-2 ring-[#0E7C7B]/30 bg-[#0E7C7B]/5" : "border-slate-200";

  const certifie = async () => {
    if (form.text.trim().length < 100) { toast.error("Texte trop court (100 caractères minimum)."); return; }
    setCertLoading(true); setCert(null);
    try { const { data } = await api.post("/recherche/ai/plagiat-certifie", { text: form.text }); setCert(data); }
    catch (e) { toast.error(apiError(e, "Détection certifiée indisponible")); }
    finally { setCertLoading(false); }
  };

  const downloadReport = async () => {
    if (!cert) return;
    setReportLoading(true);
    try {
      const title = form.topic ? `${KIND_TITLES[form.kind] || "Document"} — ${form.topic}` : "Document de recherche";
      const res = await api.post("/recherche/ai/plagiat-report",
        { score: cert.score, threshold: Number(threshold), sources: cert.sources || [], title },
        { responseType: "blob" });
      const u = URL.createObjectURL(res.data); const a = document.createElement("a");
      a.href = u; a.download = "rapport-plagiat.pdf"; a.click(); URL.revokeObjectURL(u);
    } catch (e) { toast.error(apiError(e, "Rapport indisponible")); }
    finally { setReportLoading(false); }
  };

  const verifyReform = async () => {
    if ((result || "").trim().length < 100) { toast.error("Texte reformulé trop court (100 caractères minimum)."); return; }
    setReformCertLoading(true); setReformCert(null);
    try {
      const { data } = await api.post("/recherche/ai/plagiat-certifie", { text: result });
      setReformCert(data);
      toast.success("Vérification d'intégrité terminée");
    } catch (e) { toast.error(apiError(e, "Vérification d'intégrité indisponible")); }
    finally { setReformCertLoading(false); }
  };

  const verifyRedaction = async () => {
    if ((result || "").trim().length < 100) { toast.error("Document trop court (100 caractères minimum)."); return; }
    setRedCertLoading(true); setRedCert(null);
    try {
      const { data } = await api.post("/recherche/ai/plagiat-certifie", { text: result });
      setRedCert(data);
      toast.success("Vérification d'intégrité terminée");
    } catch (e) { toast.error(apiError(e, "Vérification d'intégrité indisponible")); }
    finally { setRedCertLoading(false); }
  };

  const checkHumanScore = async () => {
    if ((result || "").trim().length < 300) { toast.error("Texte trop court (300 caractères minimum pour le score d'humanité)."); return; }
    setHumanScoreLoading(true); setHumanScore(null);
    try {
      const { data } = await api.post("/recherche/ai/human-score", { text: result });
      setHumanScore(data.score);
      toast.success("Score d'humanité calculé");
    } catch (e) { toast.error(apiError(e, "Score d'humanité indisponible")); }
    finally { setHumanScoreLoading(false); }
  };

  const exportDoc = async (format) => {
    setExporting(format);
    try {
      const isReform = tool === "reformulation";
      const isHuman = tool === "humanisation";
      const title = isReform ? "Texte reformulé" : isHuman ? "Texte humanisé"
        : (form.topic ? `${KIND_TITLES[form.kind] || "Document"} — ${form.topic}` : "Document de recherche");
      const body = { title, content: result, format, draft: !(isReform || isHuman) };
      if (!isHuman) {
        const activeCert = isReform ? reformCert : redCert;
        const activeThreshold = isReform ? reformThreshold : redThreshold;
        const activeAttach = isReform ? reformAttach : redAttach;
        if (activeCert && activeAttach) {
          body.attach_plagiat = true;
          body.plagiat_score = activeCert.score;
          body.plagiat_threshold = Number(activeThreshold);
          body.plagiat_sources = activeCert.sources || [];
        }
      }
      const res = await api.post("/recherche/ai/export", body, { responseType: "blob" });
      const u = URL.createObjectURL(res.data); const a = document.createElement("a");
      a.href = u; a.download = format === "docx" ? "document-recherche.docx" : "document-recherche.pdf"; a.click(); URL.revokeObjectURL(u);
    } catch (e) { toast.error(apiError(e)); } finally { setExporting(""); }
  };

  const run = async () => {
    setStatus("running"); setResult(""); setReformCert(null); setRedCert(null); setHumanScore(null);
    try {
      const { data } = await api.post("/recherche/ai/start", { tool, mode: reformMode, ...form });
      const jid = data.job_id;
      const poll = setInterval(async () => {
        try {
          const { data: j } = await api.get(`/recherche/ai/job/${jid}`);
          if (j.status === "done") { clearInterval(poll); setResult(j.result); setStatus("done"); }
          else if (j.status === "error") { clearInterval(poll); toast.error(j.error || "Erreur IA"); setStatus("error"); }
        } catch { clearInterval(poll); setStatus("error"); }
      }, 3000);
    } catch (e) { toast.error(apiError(e)); setStatus("idle"); }
  };

  const attachedCard = attachedList.length > 0 && (
    <div data-testid="rs-attached-preview" className="rounded-lg border border-[#0E7C7B]/30 bg-[#0E7C7B]/5 p-3 text-xs">
      <div className="flex items-center justify-between gap-2">
        <span className="font-semibold text-navy" data-testid="rs-attached-count">{attachedList.length} fichier(s) joint(s) · {attachedList.reduce((n, it) => n + it.chars, 0)} caractères</span>
        <button type="button" onClick={clearAttached} data-testid="rs-attached-clear" className="text-slate-400 transition hover:text-red-500">Tout retirer</button>
      </div>
      <ul className="mt-2 space-y-1">
        {attachedList.map((it, i) => (
          <li key={i} className="flex items-center justify-between gap-2 rounded bg-white px-2 py-1" data-testid={`rs-attached-item-${i}`}>
            <span className="flex min-w-0 items-center gap-1.5 text-navy"><FileText size={13} className="shrink-0 text-[#0E7C7B]" /> <span className="truncate" data-testid={`rs-attached-name-${i}`}>{it.name}</span></span>
            <span className="flex shrink-0 items-center gap-2">
              <span className="text-slate-400">{Math.max(1, Math.round(it.size / 1024))} Ko · {it.chars} car.</span>
              <button type="button" onClick={() => removeAttached(i)} data-testid={`rs-attached-remove-${i}`} className="text-slate-400 transition hover:text-red-500" title="Retirer ce fichier"><XCircle size={14} /></button>
            </span>
          </li>
        ))}
      </ul>
      <div className="mt-1.5 max-h-24 overflow-y-auto whitespace-pre-wrap rounded bg-white p-2 text-slate-600" data-testid="rs-attached-snippet">{(form.text || "").slice(0, 500)}{(form.text || "").length > 500 ? "…" : ""}</div>
    </div>
  );

  return (
    <div className="grid gap-6 lg:grid-cols-[300px_1fr]">
      <div className="space-y-3">
        {AI_TOOLS.map((t) => (
          <button key={t.key} onClick={() => { setTool(t.key); setResult(""); setStatus("idle"); setAttachedList([]); }} data-testid={`rs-ai-${t.key}`}
            className={`w-full rounded-2xl border p-4 text-left transition ${tool === t.key ? "border-[#0E7C7B] bg-[#0E7C7B]/5" : "border-slate-200 bg-white"}`}>
            <p className="font-semibold text-navy">{t.label}</p>
            <p className="text-xs text-slate-500">{t.desc}</p>
          </button>
        ))}
      </div>
      <div className="rounded-2xl border border-slate-200 bg-white p-5">
        {tool === "redaction" ? (
          <div className="space-y-3">
            <select value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })} data-testid="rs-ai-kind" className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-navy">
              <option value="protocole">Protocole de recherche</option>
              <option value="memoire">Mémoire de recherche (plan détaillé)</option>
              <option value="publication">Article scientifique (IMRaD)</option>
            </select>
            <Input value={form.topic} onChange={(e) => setForm({ ...form, topic: e.target.value })} placeholder="Sujet / question de recherche *" data-testid="rs-ai-topic" />
            <Input value={form.discipline} onChange={(e) => setForm({ ...form, discipline: e.target.value })} placeholder="Discipline (ex : Épidémiologie)" data-testid="rs-ai-discipline" />
            <textarea value={form.details} onChange={(e) => setForm({ ...form, details: e.target.value })} rows={3} placeholder="Précisions (population, objectifs…)" className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" data-testid="rs-ai-details" />
          </div>
        ) : tool === "reformulation" ? (
          <div className="space-y-3">
            <select value={reformMode} onChange={(e) => setReformMode(e.target.value)} data-testid="rs-reform-mode" className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-navy">
              {REFORM_MODES.map((m) => <option key={m.v} value={m.v}>{m.l}</option>)}
            </select>
            <textarea {...dropProps} value={form.text} onChange={(e) => setForm({ ...form, text: e.target.value })} rows={9} placeholder="Collez votre texte à reformuler, ou glissez-déposez un fichier ici…" className={`w-full rounded-lg border px-3 py-2 text-sm transition ${dropCls}`} data-testid="rs-ai-text" />
            <label className="inline-flex cursor-pointer items-center gap-2 rounded-full border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50" data-testid="rs-reform-attach-label">
              {attaching ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />} Joindre des fichiers (.pdf, .docx, .txt)
              <input type="file" multiple accept=".pdf,.docx,.txt" onChange={attachFile} className="hidden" data-testid="rs-reform-attach" />
            </label>
            {attachedCard}
          </div>
        ) : tool === "humanisation" ? (
          <div className="space-y-3">
            <textarea {...dropProps} value={form.text} onChange={(e) => setForm({ ...form, text: e.target.value })} rows={9} placeholder="Collez le texte à humaniser, ou glissez-déposez un fichier ici…" className={`w-full rounded-lg border px-3 py-2 text-sm transition ${dropCls}`} data-testid="rs-ai-text" />
            <label className="inline-flex cursor-pointer items-center gap-2 rounded-full border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50" data-testid="rs-human-attach-label">
              {attaching ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />} Joindre des fichiers (.pdf, .docx, .txt)
              <input type="file" multiple accept=".pdf,.docx,.txt" onChange={attachFile} className="hidden" data-testid="rs-human-attach" />
            </label>
            {attachedCard}
          </div>
        ) : (
          <div className="space-y-3">
            <textarea {...dropProps} value={form.text} onChange={(e) => setForm({ ...form, text: e.target.value })} rows={8} placeholder={tool === "evaluation" ? "Collez le texte de votre mémoire / document à évaluer, glissez-déposez ou joignez un fichier…" : "Collez votre texte à vérifier, glissez-déposez ou joignez un fichier…"} className={`w-full rounded-lg border px-3 py-2 text-sm transition ${dropCls}`} data-testid="rs-ai-text" />
            <label className="inline-flex cursor-pointer items-center gap-2 rounded-full border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50" data-testid="rs-analyze-attach-label">
              {attaching ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />} Joindre des fichiers (.pdf, .docx, .xlsx, .txt)
              <input type="file" multiple accept=".pdf,.docx,.xlsx,.xls,.txt" onChange={attachFile} className="hidden" data-testid="rs-analyze-attach" />
            </label>
            {attachedCard}
          </div>
        )}
        <Button onClick={run} disabled={status === "running"} data-testid="rs-ai-run" className="mt-3 rounded-full bg-navy text-white hover:bg-navy-light">
          {status === "running" ? <><Loader2 size={15} className="mr-1 animate-spin" /> {tool === "reformulation" ? "Reformulation…" : tool === "humanisation" ? "Humanisation…" : "Génération en cours…"}</> : <><Sparkles size={15} className="mr-1" /> {tool === "reformulation" ? "Reformuler le texte" : tool === "humanisation" ? "Humaniser le texte" : "Lancer l'IA"}</>}
        </Button>
        {tool === "plagiat" && <Button onClick={certifie} disabled={certLoading} data-testid="rs-cert-run" variant="outline" className="ml-2 mt-3 rounded-full border-[#0E7C7B] text-[#0E7C7B]">{certLoading ? <Loader2 size={15} className="mr-1 animate-spin" /> : <ShieldCheck size={15} className="mr-1" />} Analyse certifiée</Button>}
        {tool === "plagiat" && (
          <div className="ml-2 mt-3 inline-flex items-center gap-2 align-middle" data-testid="rs-threshold-wrap">
            <label className="text-xs text-slate-500" htmlFor="rs-threshold">Seuil d'alerte</label>
            <input id="rs-threshold" type="number" min={1} max={100} value={threshold}
              onChange={(e) => setThreshold(Math.min(100, Math.max(1, Number(e.target.value) || 0)))}
              data-testid="rs-threshold-input"
              className="w-16 rounded-lg border border-slate-200 px-2 py-1 text-sm text-navy" />
            <span className="text-xs text-slate-500">%</span>
          </div>
        )}
        {cert && (() => {
          const s = cert.score != null ? Number(cert.score) : null;
          const conform = s != null && s <= Number(threshold);
          const badgeCls = s == null ? "border-slate-300 bg-slate-50 text-slate-600"
            : conform ? "border-emerald-300 bg-emerald-50 text-emerald-700"
            : "border-red-300 bg-red-50 text-red-700";
          return (
            <div className="mt-4 rounded-xl border border-[#0E7C7B]/30 bg-[#0E7C7B]/5 p-4 text-sm" data-testid="rs-cert-result">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <p className="font-semibold text-navy">Similarité détectée : <span className="text-[#0E7C7B]">{s != null ? `${s}%` : "—"}</span></p>
                <span data-testid="rs-cert-badge" className={`inline-flex items-center gap-1 rounded-full border px-3 py-1 text-xs font-semibold ${badgeCls}`}>
                  {s == null ? "—" : conform ? <><CheckCircle2 size={13} /> Conforme (≤ {threshold}%)</> : <><AlertTriangle size={13} /> À revoir (&gt; {threshold}%)</>}
                </span>
              </div>
              {cert.sources?.length > 0 && <ul className="mt-2 list-disc space-y-1 pl-5 text-xs text-slate-600">{cert.sources.map((s2, i) => <li key={i}><a href={s2.url} target="_blank" rel="noreferrer" className="text-[#0E7C7B] hover:underline">{s2.title || s2.url}</a>{s2.score != null ? ` — ${s2.score}%` : ""}</li>)}</ul>}
              <Button onClick={downloadReport} disabled={reportLoading} data-testid="rs-cert-report" variant="outline" className="mt-3 rounded-full border-[#0E7C7B] text-xs text-[#0E7C7B]">
                {reportLoading ? <Loader2 size={13} className="mr-1 animate-spin" /> : <FileDown size={13} className="mr-1" />} Télécharger le rapport PDF
              </Button>
            </div>
          );
        })()}
        {status === "running" && <p className="mt-2 text-xs text-slate-400">{tool === "reformulation" ? "L'IA reformule votre texte, cela peut prendre jusqu'à une minute…" : tool === "humanisation" ? "L'IA humanise votre texte, cela peut prendre jusqu'à une minute…" : "L'IA rédige votre document, cela peut prendre jusqu'à une minute…"}</p>}
        {result && tool === "reformulation" ? (
          <div className="mt-4" data-testid="rs-reform-result">
            <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
              <p className="text-sm font-semibold text-navy">Résultat de la reformulation</p>
              <div className="inline-flex rounded-full border border-slate-200 bg-slate-50 p-1" data-testid="rs-reform-viewmode">
                <button type="button" onClick={() => setShowDiff(true)} data-testid="rs-reform-view-diff"
                  className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-medium transition ${showDiff ? "bg-navy text-white shadow" : "text-slate-500 hover:text-navy"}`}>
                  <GitCompare size={13} /> Différences
                </button>
                <button type="button" onClick={() => setShowDiff(false)} data-testid="rs-reform-view-side"
                  className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-medium transition ${!showDiff ? "bg-navy text-white shadow" : "text-slate-500 hover:text-navy"}`}>
                  <Columns size={13} /> Côte à côte
                </button>
              </div>
            </div>
            {showDiff ? (
              <div>
                <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-[#0E7C7B]">Comparaison avant / après</p>
                <WordDiff original={form.text} revised={result} testid="rs-reform-diff"
                  className="max-h-[560px] overflow-y-auto rounded-xl border border-[#0E7C7B]/30 bg-white p-4 text-sm text-slate-700" />
              </div>
            ) : (
              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-400">Texte original</p>
                  <div className="max-h-[520px] overflow-y-auto whitespace-pre-wrap rounded-xl border border-slate-200 bg-white p-4 text-sm text-slate-600" data-testid="rs-reform-original">{form.text}</div>
                </div>
                <div>
                  <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-[#0E7C7B]">Texte reformulé</p>
                  <div className="max-h-[520px] overflow-y-auto whitespace-pre-wrap rounded-xl border border-[#0E7C7B]/30 bg-[#0E7C7B]/5 p-4 text-sm text-slate-700" data-testid="rs-reform-output">{result}</div>
                </div>
              </div>
            )}
            <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50 p-4" data-testid="rs-reform-integrity">
              <div className="flex flex-wrap items-center gap-3">
                <Button onClick={verifyReform} disabled={reformCertLoading} data-testid="rs-reform-verify" className="rounded-full bg-[#0E7C7B] text-white hover:bg-[#0c6a69]">
                  {reformCertLoading ? <Loader2 size={15} className="mr-1 animate-spin" /> : <ShieldCheck size={15} className="mr-1" />} Vérifier l'intégrité
                </Button>
                <div className="inline-flex items-center gap-2" data-testid="rs-reform-threshold-wrap">
                  <label className="text-xs text-slate-500" htmlFor="rs-reform-threshold">Seuil d'alerte</label>
                  <input id="rs-reform-threshold" type="number" min={1} max={100} value={reformThreshold}
                    onChange={(e) => setReformThreshold(Math.min(100, Math.max(1, Number(e.target.value) || 0)))}
                    data-testid="rs-reform-threshold-input"
                    className="w-16 rounded-lg border border-slate-200 px-2 py-1 text-sm text-navy" />
                  <span className="text-xs text-slate-500">%</span>
                </div>
                <span className="text-xs text-slate-400">Détection certifiée Winston AI — sans copier-coller</span>
              </div>
              {reformCertLoading && <p className="mt-2 text-xs text-slate-400">Analyse d'intégrité en cours…</p>}
              {reformCert && (() => {
                const s = reformCert.score != null ? Number(reformCert.score) : null;
                const conform = s != null && s <= Number(reformThreshold);
                const badgeCls = s == null ? "border-slate-300 bg-slate-50 text-slate-600"
                  : conform ? "border-emerald-300 bg-emerald-50 text-emerald-700"
                  : "border-red-300 bg-red-50 text-red-700";
                return (
                  <div className="mt-3 rounded-xl border border-[#0E7C7B]/30 bg-white p-4 text-sm" data-testid="rs-reform-cert-result">
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <p className="font-semibold text-navy">Similarité détectée : <span className="text-[#0E7C7B]">{s != null ? `${s}%` : "—"}</span></p>
                      <span data-testid="rs-reform-cert-badge" className={`inline-flex items-center gap-1 rounded-full border px-3 py-1 text-xs font-semibold ${badgeCls}`}>
                        {s == null ? "—" : conform ? <><CheckCircle2 size={13} /> Conforme (≤ {reformThreshold}%)</> : <><AlertTriangle size={13} /> À revoir (&gt; {reformThreshold}%)</>}
                      </span>
                    </div>
                    {reformCert.sources?.length > 0 && <ul className="mt-2 list-disc space-y-1 pl-5 text-xs text-slate-600">{reformCert.sources.map((s2, i) => <li key={i}><a href={s2.url} target="_blank" rel="noreferrer" className="text-[#0E7C7B] hover:underline">{s2.title || s2.url}</a>{s2.score != null ? ` — ${s2.score}%` : ""}</li>)}</ul>}
                    <label className="mt-3 flex items-center gap-2 text-xs text-slate-600" data-testid="rs-reform-attach-label">
                      <input type="checkbox" checked={reformAttach} onChange={(e) => setReformAttach(e.target.checked)} data-testid="rs-reform-attach-plagiat" className="h-4 w-4 rounded border-slate-300 text-[#0E7C7B]" />
                      Joindre le rapport d'intégrité en annexe du document exporté
                    </label>
                  </div>
                );
              })()}
            </div>
            <div className="mt-3 flex gap-2" data-testid="rs-reform-export">
              <Button onClick={() => exportDoc("pdf")} disabled={!!exporting} data-testid="rs-export-pdf" variant="outline" className="rounded-full border-[#0E7C7B] text-xs text-[#0E7C7B]">{exporting === "pdf" ? <Loader2 size={13} className="mr-1 animate-spin" /> : <Download size={13} className="mr-1" />} Télécharger en PDF</Button>
              <Button onClick={() => exportDoc("docx")} disabled={!!exporting} data-testid="rs-export-docx" variant="outline" className="rounded-full border-[#0E7C7B] text-xs text-[#0E7C7B]">{exporting === "docx" ? <Loader2 size={13} className="mr-1 animate-spin" /> : <Download size={13} className="mr-1" />} Télécharger en Word</Button>
            </div>
          </div>
        ) : result && tool === "humanisation" ? (
          <div className="mt-4" data-testid="rs-human-result">
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-400">Texte original</p>
                <div className="max-h-[520px] overflow-y-auto whitespace-pre-wrap rounded-xl border border-slate-200 bg-white p-4 text-sm text-slate-600" data-testid="rs-human-original">{form.text}</div>
              </div>
              <div>
                <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-[#0E7C7B]">Texte humanisé</p>
                <div className="max-h-[520px] overflow-y-auto whitespace-pre-wrap rounded-xl border border-[#0E7C7B]/30 bg-[#0E7C7B]/5 p-4 text-sm text-slate-700" data-testid="rs-human-output">{result}</div>
              </div>
            </div>
            <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50 p-4" data-testid="rs-human-score-panel">
              <div className="flex flex-wrap items-center gap-3">
                <Button onClick={checkHumanScore} disabled={humanScoreLoading} data-testid="rs-human-score-run" className="rounded-full bg-[#0E7C7B] text-white hover:bg-[#0c6a69]">
                  {humanScoreLoading ? <Loader2 size={15} className="mr-1 animate-spin" /> : <ShieldCheck size={15} className="mr-1" />} Calculer le score d'humanité (Winston AI)
                </Button>
                <span className="text-xs text-slate-400">Score « Human Score » de détection IA — 0 = IA, 100 = humain (min. 300 caractères)</span>
              </div>
              {humanScoreLoading && <p className="mt-2 text-xs text-slate-400">Analyse Winston AI en cours…</p>}
              {humanScore != null && (() => {
                const hs = Number(humanScore);
                const cls = hs >= 80 ? "border-emerald-300 bg-emerald-50 text-emerald-700"
                  : hs >= 50 ? "border-amber-300 bg-amber-50 text-amber-700"
                  : "border-red-300 bg-red-50 text-red-700";
                return (
                  <div className="mt-3 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-[#0E7C7B]/30 bg-white p-4 text-sm" data-testid="rs-human-score-result">
                    <p className="font-semibold text-navy">Score d'humanité : <span className="text-[#0E7C7B]">{hs}%</span></p>
                    <span data-testid="rs-human-score-badge" className={`inline-flex items-center gap-1 rounded-full border px-3 py-1 text-xs font-semibold ${cls}`}>
                      {hs >= 80 ? <><CheckCircle2 size={13} /> Rendu très humain</> : hs >= 50 ? <><AlertTriangle size={13} /> Rendu partiellement humain</> : <><AlertTriangle size={13} /> Rendu perçu comme IA</>}
                    </span>
                  </div>
                );
              })()}
            </div>
            <div className="mt-3 flex gap-2" data-testid="rs-human-export">
              <Button onClick={() => exportDoc("pdf")} disabled={!!exporting} data-testid="rs-export-pdf" variant="outline" className="rounded-full border-[#0E7C7B] text-xs text-[#0E7C7B]">{exporting === "pdf" ? <Loader2 size={13} className="mr-1 animate-spin" /> : <Download size={13} className="mr-1" />} Télécharger en PDF</Button>
              <Button onClick={() => exportDoc("docx")} disabled={!!exporting} data-testid="rs-export-docx" variant="outline" className="rounded-full border-[#0E7C7B] text-xs text-[#0E7C7B]">{exporting === "docx" ? <Loader2 size={13} className="mr-1 animate-spin" /> : <Download size={13} className="mr-1" />} Télécharger en Word</Button>
            </div>
          </div>
        ) : result && (
          <div className="mt-4 max-h-[520px] overflow-y-auto whitespace-pre-wrap rounded-xl bg-slate-50 p-4 text-sm text-slate-700" data-testid="rs-ai-result">{result}</div>
        )}
        {result && tool === "redaction" && (
          <div className="mt-3 space-y-3" data-testid="rs-ai-export">
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-4" data-testid="rs-red-integrity">
              <div className="flex flex-wrap items-center gap-3">
                <Button onClick={verifyRedaction} disabled={redCertLoading} data-testid="rs-red-verify" className="rounded-full bg-[#0E7C7B] text-white hover:bg-[#0c6a69]">
                  {redCertLoading ? <Loader2 size={15} className="mr-1 animate-spin" /> : <ShieldCheck size={15} className="mr-1" />} Vérifier l'intégrité
                </Button>
                <div className="inline-flex items-center gap-2" data-testid="rs-red-threshold-wrap">
                  <label className="text-xs text-slate-500" htmlFor="rs-red-threshold">Seuil d'alerte</label>
                  <input id="rs-red-threshold" type="number" min={1} max={100} value={redThreshold}
                    onChange={(e) => setRedThreshold(Math.min(100, Math.max(1, Number(e.target.value) || 0)))}
                    data-testid="rs-red-threshold-input"
                    className="w-16 rounded-lg border border-slate-200 px-2 py-1 text-sm text-navy" />
                  <span className="text-xs text-slate-500">%</span>
                </div>
                <span className="text-xs text-slate-400">Détection certifiée Winston AI — sans copier-coller</span>
              </div>
              {redCertLoading && <p className="mt-2 text-xs text-slate-400">Analyse d'intégrité en cours…</p>}
              {redCert && (() => {
                const s = redCert.score != null ? Number(redCert.score) : null;
                const conform = s != null && s <= Number(redThreshold);
                const badgeCls = s == null ? "border-slate-300 bg-slate-50 text-slate-600"
                  : conform ? "border-emerald-300 bg-emerald-50 text-emerald-700"
                  : "border-red-300 bg-red-50 text-red-700";
                return (
                  <div className="mt-3 rounded-xl border border-[#0E7C7B]/30 bg-white p-4 text-sm" data-testid="rs-red-cert-result">
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <p className="font-semibold text-navy">Similarité détectée : <span className="text-[#0E7C7B]">{s != null ? `${s}%` : "—"}</span></p>
                      <span data-testid="rs-red-cert-badge" className={`inline-flex items-center gap-1 rounded-full border px-3 py-1 text-xs font-semibold ${badgeCls}`}>
                        {s == null ? "—" : conform ? <><CheckCircle2 size={13} /> Conforme (≤ {redThreshold}%)</> : <><AlertTriangle size={13} /> À revoir (&gt; {redThreshold}%)</>}
                      </span>
                    </div>
                    {redCert.sources?.length > 0 && <ul className="mt-2 list-disc space-y-1 pl-5 text-xs text-slate-600">{redCert.sources.map((s2, i) => <li key={i}><a href={s2.url} target="_blank" rel="noreferrer" className="text-[#0E7C7B] hover:underline">{s2.title || s2.url}</a>{s2.score != null ? ` — ${s2.score}%` : ""}</li>)}</ul>}
                    <label className="mt-3 flex items-center gap-2 text-xs text-slate-600" data-testid="rs-red-attach-label">
                      <input type="checkbox" checked={redAttach} onChange={(e) => setRedAttach(e.target.checked)} data-testid="rs-red-attach-plagiat" className="h-4 w-4 rounded border-slate-300 text-[#0E7C7B]" />
                      Joindre le rapport d'intégrité en annexe du document exporté
                    </label>
                  </div>
                );
              })()}
            </div>
            <div className="flex gap-2">
              <Button onClick={() => exportDoc("pdf")} disabled={!!exporting} data-testid="rs-export-pdf" variant="outline" className="rounded-full border-[#0E7C7B] text-xs text-[#0E7C7B]">{exporting === "pdf" ? <Loader2 size={13} className="mr-1 animate-spin" /> : <Download size={13} className="mr-1" />} Exporter en PDF</Button>
              <Button onClick={() => exportDoc("docx")} disabled={!!exporting} data-testid="rs-export-docx" variant="outline" className="rounded-full border-[#0E7C7B] text-xs text-[#0E7C7B]">{exporting === "docx" ? <Loader2 size={13} className="mr-1 animate-spin" /> : <Download size={13} className="mr-1" />} Exporter en Word</Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function SubmitForm({ themes }) {
  const [f, setF] = useState({ title: "", authors: "", theme: "Autre", year: "", abstract: "", keywords: "" });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!f.title || !f.authors || !file) { toast.error("Titre, auteurs et fichier PDF requis."); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      Object.entries(f).forEach(([k, v]) => fd.append(k, v));
      fd.append("file", file);
      await api.post("/recherche/papers", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Article soumis ! Il sera visible après validation.");
      setF({ title: "", authors: "", theme: "Autre", year: "", abstract: "", keywords: "" }); setFile(null);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <div className="max-w-2xl rounded-2xl border border-slate-200 bg-white p-6" data-testid="rs-submit-form">
      <p className="mb-4 text-sm text-slate-500">Soumettez votre article. Après validation par l'équipe, il rejoint la bibliothèque avec un résumé IA.</p>
      <div className="grid gap-3">
        <Input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} placeholder="Titre *" data-testid="rs-submit-title" />
        <Input value={f.authors} onChange={(e) => setF({ ...f, authors: e.target.value })} placeholder="Auteur(s) *" data-testid="rs-submit-authors" />
        <div className="grid grid-cols-2 gap-3">
          <select value={f.theme} onChange={(e) => setF({ ...f, theme: e.target.value })} data-testid="rs-submit-theme" className="rounded-lg border border-slate-200 px-3 py-2 text-sm text-navy">{themes.map((t) => <option key={t} value={t}>{t}</option>)}</select>
          <Input value={f.year} onChange={(e) => setF({ ...f, year: e.target.value })} placeholder="Année" data-testid="rs-submit-year" />
        </div>
        <textarea value={f.abstract} onChange={(e) => setF({ ...f, abstract: e.target.value })} rows={3} placeholder="Résumé / abstract" className="rounded-lg border border-slate-200 px-3 py-2 text-sm" data-testid="rs-submit-abstract" />
        <Input value={f.keywords} onChange={(e) => setF({ ...f, keywords: e.target.value })} placeholder="Mots-clés (séparés par des virgules)" data-testid="rs-submit-keywords" />
        <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-dashed border-slate-300 px-3 py-2 text-sm text-slate-600" data-testid="rs-submit-file-label">
          <FileText size={15} className="text-[#0E7C7B]" /> {file ? file.name : "Choisir le fichier PDF *"}
          <input type="file" accept=".pdf,.doc,.docx" className="hidden" onChange={(e) => setFile(e.target.files?.[0])} data-testid="rs-submit-file" />
        </label>
        <Button onClick={submit} disabled={busy} data-testid="rs-submit-btn" className="rounded-full bg-[#0E7C7B] text-white hover:bg-[#0b6463]">{busy ? <Loader2 size={15} className="mr-1 animate-spin" /> : <Upload size={15} className="mr-1" />} Soumettre l'article</Button>
      </div>
    </div>
  );
}

function Stats() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/recherche/stats").then(({ data }) => setS(data)).catch(() => setS(null)); }, []);
  if (!s) return <div className="flex justify-center py-12"><Loader2 className="animate-spin text-[#0E7C7B]" /></div>;
  const cards = [["Publications", s.total_approved], ["Téléchargements", s.total_downloads], ["En attente", s.pending]];
  return (
    <div data-testid="rs-stats">
      <div className="grid gap-4 sm:grid-cols-3">
        {cards.map(([l, v]) => <div key={l} className="rounded-2xl border border-slate-200 bg-white p-5"><p className="text-3xl font-bold text-navy">{v}</p><p className="text-sm text-slate-500">{l}</p></div>)}
      </div>
      <div className="mt-6 grid gap-6 md:grid-cols-2">
        <div className="rounded-2xl border border-slate-200 bg-white p-5"><h3 className="mb-3 font-semibold text-navy">Par thème</h3>{s.by_theme.length === 0 ? <p className="text-sm text-slate-400">—</p> : s.by_theme.map((t) => <div key={t.theme} className="mb-1 flex items-center gap-2 text-sm"><span className="w-40 truncate text-slate-600">{t.theme}</span><div className="h-2 flex-1 rounded bg-slate-100"><div className="h-2 rounded bg-[#0E7C7B]" style={{ width: `${Math.min(100, t.count * 20)}%` }} /></div><span className="w-6 text-right text-navy">{t.count}</span></div>)}</div>
        <div className="rounded-2xl border border-slate-200 bg-white p-5"><h3 className="mb-3 font-semibold text-navy">Par année</h3>{s.by_year.length === 0 ? <p className="text-sm text-slate-400">—</p> : s.by_year.map((y) => <div key={y.year} className="mb-1 flex justify-between text-sm"><span className="text-slate-600">{y.year}</span><span className="font-semibold text-navy">{y.count}</span></div>)}</div>
      </div>
    </div>
  );
}

function blobDownload(res, filename) {
  const u = URL.createObjectURL(res.data); const a = document.createElement("a");
  a.href = u; a.download = filename; a.click(); URL.revokeObjectURL(u);
}

const STATUS_MAP = {
  pending: ["En attente de validation", "border-amber-300 bg-amber-50 text-amber-700"],
  approved: ["Validé", "border-emerald-300 bg-emerald-50 text-emerald-700"],
  rejected: ["Rejeté", "border-red-300 bg-red-50 text-red-700"],
};

function MySubmissions() {
  const [papers, setPapers] = useState(null);
  const [dl, setDl] = useState("");
  useEffect(() => { api.get("/recherche/mine").then(({ data }) => setPapers(data.papers)).catch(() => setPapers([])); }, []);
  const download = async (id, kind) => {
    setDl(`${id}-${kind}`);
    try {
      const url = kind === "cert" ? `/recherche/papers/${id}/certificate` : `/recherche/papers/${id}/plagiat-report`;
      const res = await api.get(url, { responseType: "blob" });
      blobDownload(res, kind === "cert" ? "certificat-validation.pdf" : "rapport-plagiat.pdf");
    } catch (e) { toast.error(apiError(e, "Téléchargement indisponible")); } finally { setDl(""); }
  };
  if (!papers) return <div className="flex justify-center py-12"><Loader2 className="animate-spin text-[#0E7C7B]" /></div>;
  if (papers.length === 0) return <p className="py-12 text-center text-sm text-slate-400" data-testid="rs-mine-empty">Vous n'avez soumis aucun article pour l'instant.</p>;
  return (
    <div className="space-y-3" data-testid="rs-mine">
      {papers.map((p) => {
        const [lbl, cls] = STATUS_MAP[p.status] || ["—", "border-slate-200 bg-slate-50 text-slate-500"];
        const checked = (p.plagiat || {}).status === "checked";
        return (
          <div key={p.id} className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-slate-200 bg-white p-4" data-testid={`rs-mine-${p.id}`}>
            <div className="min-w-0">
              <p className="font-semibold text-navy">{p.title}</p>
              <p className="text-xs text-slate-500">{p.theme}{p.year ? ` · ${p.year}` : ""} · soumis le {fmtDate(p.created_at)}</p>
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <span className={`rounded-full border px-2.5 py-0.5 text-[11px] font-semibold ${cls}`} data-testid={`rs-mine-status-${p.id}`}>{lbl}</span>
                <PlagiatBadge p={p} />
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              {checked && (
                <Button onClick={() => download(p.id, "report")} disabled={dl === `${p.id}-report`} data-testid={`rs-mine-report-${p.id}`} variant="outline" className="rounded-full border-slate-300 text-xs text-slate-600">{dl === `${p.id}-report` ? <Loader2 size={14} className="mr-1 animate-spin" /> : <FileDown size={14} className="mr-1" />} Rapport de plagiat</Button>
              )}
              {p.status === "approved" && (
                <Button onClick={() => download(p.id, "cert")} disabled={dl === `${p.id}-cert`} data-testid={`rs-mine-cert-${p.id}`} variant="outline" className="rounded-full border-[#0E7C7B] text-xs text-[#0E7C7B]">{dl === `${p.id}-cert` ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Award size={14} className="mr-1" />} Mon certificat</Button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// Catalogue des tests statistiques, organisé en 3 familles (3 listes déroulantes).
// mode → variables attendues côté UI/back :
//   num+group : 1 var numérique + 1 var de groupe (qualitative)
//   paired2   : 2 colonnes numériques appariées (avant / après)
//   pairedN   : ≥3 colonnes numériques appariées (mesures répétées)
//   num+num   : 2 var numériques
//   cat+cat   : 2 var qualitatives
//   cat1      : 1 var qualitative
//   catpair2  : 2 var qualitatives binaires appariées
//   num*/auto : sélection automatique
const TEST_FAMILIES = [
  {
    key: "quant",
    label: "Variables quantitatives",
    tests: [
      { v: "ttest", l: "Test t de Student (échantillons indépendants)", mode: "num+group" },
      { v: "ttest_paired", l: "Test t de Student apparié (échantillons appariés)", mode: "paired2" },
      { v: "anova", l: "ANOVA à un facteur (≥3 groupes)", mode: "num+group" },
      { v: "rm_anova", l: "ANOVA sur mesures répétées (≥3 mesures)", mode: "pairedN" },
      { v: "mannwhitney", l: "Mann-Whitney (2 groupes indépendants)", mode: "num+group" },
      { v: "wilcoxon", l: "Wilcoxon (échantillons appariés)", mode: "paired2" },
      { v: "kruskal", l: "Kruskal-Wallis (≥3 groupes)", mode: "num+group" },
      { v: "friedman", l: "Friedman (≥3 mesures répétées)", mode: "pairedN" },
    ],
  },
  {
    key: "qual",
    label: "Variables qualitatives",
    tests: [
      { v: "chi2", l: "Khi² de Pearson (indépendance)", mode: "cat+cat" },
      { v: "chi2_gof", l: "Khi² d'ajustement (conformité)", mode: "cat1" },
      { v: "fisher", l: "Test exact de Fisher (tableau 2×2)", mode: "cat+cat" },
      { v: "mcnemar", l: "Test de McNemar (données appariées)", mode: "catpair2" },
    ],
  },
  {
    key: "autres",
    label: "Autres tests (corrélations, régressions, survie, séries)",
    tests: [
      { v: "pearson", l: "Corrélation de Pearson", mode: "num+num" },
      { v: "spearman", l: "Corrélation de Spearman", mode: "num+num" },
      { v: "linreg", l: "Régression linéaire", mode: "num*" },
      { v: "logreg", l: "Régression logistique", mode: "auto" },
      { v: "survival", l: "Analyse de survie (Kaplan-Meier & Log-rank)", mode: "survival" },
      { v: "cox", l: "Régression de Cox (survie ajustée, Hazard Ratios)", mode: "cox" },
      { v: "timeseries", l: "Série temporelle (tendance, saisonnalité & prévision)", mode: "timeseries" },
    ],
  },
];
const ALL_TESTS = TEST_FAMILIES.flatMap((f) => f.tests);
const TEST_MODE = Object.fromEntries(ALL_TESTS.map((t) => [t.v, t.mode]));
const TEST_LABEL = Object.fromEntries(ALL_TESTS.map((t) => [t.v, t.l]));
const familyOf = (v) => TEST_FAMILIES.find((f) => f.tests.some((t) => t.v === v))?.key;

function CohortDashboard() {
  const [dataset, setDataset] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [mapping, setMapping] = useState({ duration: "", event: "", group: "", age: "", sex: "", time: "", value: "", covariates: [] });
  const [confidence, setConfidence] = useState(0.95);
  const [horizon, setHorizon] = useState(12);
  const [job, setJob] = useState(null);
  const [result, setResult] = useState(null);
  const [running, setRunning] = useState(false);
  const [exp, setExp] = useState("");
  const [aiStatus, setAiStatus] = useState("idle");
  const [ai, setAi] = useState(null);
  const pollRef = useRef(null);
  const aiPoll = useRef(null);
  useEffect(() => () => { pollRef.current && clearInterval(pollRef.current); aiPoll.current && clearInterval(aiPoll.current); }, []);

  const cols = dataset?.columns || [];
  const allCols = cols.map((c) => c.name);
  const numCols = cols.filter((c) => c.type === "numeric").map((c) => c.name);
  const groupCols = (() => {
    const g = cols.filter((c) => c.type === "categorical" && (c.unique == null || (c.unique >= 2 && c.unique <= 12))).map((c) => c.name);
    return g.length ? g : cols.filter((c) => c.type === "categorical").map((c) => c.name);
  })();

  const upload = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setUploading(true); setResult(null); setJob(null); setAi(null); setAiStatus("idle");
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/recherche/stats/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setDataset(data);
      try {
        const { data: det } = await api.get(`/recherche/stats/cohort/detect/${data.dataset_id}`);
        setMapping({ duration: "", event: "", group: "", age: "", sex: "", time: "", value: "", covariates: [], ...det.mapping });
      } catch { /* détection optionnelle */ }
    } catch (err) { toast.error(apiError(err, "Fichier non exploitable")); } finally { setUploading(false); e.target.value = ""; }
  };

  const setMap = (patch) => setMapping((p) => ({ ...p, ...patch }));
  const toggleCov = (name) => setMapping((p) => ({ ...p, covariates: p.covariates.includes(name) ? p.covariates.filter((x) => x !== name) : [...p.covariates, name] }));

  const ready = !!dataset && !!mapping.duration && !!mapping.event && mapping.duration !== mapping.event;

  const launch = async () => {
    if (!ready) return;
    setRunning(true); setResult(null); setAi(null); setAiStatus("idle"); setJob({ progress: 5, step: "Initialisation" });
    try {
      const { data } = await api.post("/recherche/stats/cohort", { dataset_id: dataset.dataset_id, confidence, mapping, horizon });
      pollRef.current = setInterval(async () => {
        try {
          const { data: j } = await api.get(`/recherche/stats/job/${data.job_id}`);
          setJob(j);
          if (j.status === "done") { clearInterval(pollRef.current); setResult({ ...j.result, _jid: data.job_id }); setRunning(false); }
          else if (j.status === "error") { clearInterval(pollRef.current); toast.error(j.error || "Génération échouée"); setRunning(false); }
        } catch { clearInterval(pollRef.current); setRunning(false); }
      }, 2500);
    } catch (err) { toast.error(apiError(err, "Impossible de générer le tableau de bord")); setRunning(false); }
  };

  const runAi = async () => {
    if (!result?._jid) return;
    setAiStatus("running"); setAi(null);
    try {
      await api.post("/recherche/stats/ai-analysis", { job_id: result._jid });
      aiPoll.current = setInterval(async () => {
        try {
          const { data } = await api.get(`/recherche/stats/ai-analysis/${result._jid}`);
          if (data.status === "done") { clearInterval(aiPoll.current); setAi(data.data); setAiStatus("done"); }
          else if (data.status === "error") { clearInterval(aiPoll.current); toast.error(data.error || "Analyse IA échouée"); setAiStatus("idle"); }
        } catch { clearInterval(aiPoll.current); setAiStatus("idle"); }
      }, 3000);
    } catch (err) { toast.error(apiError(err, "Analyse IA indisponible")); setAiStatus("idle"); }
  };

  const exportDoc = async (format) => {
    if (!result?._jid) return;
    setExp(format);
    try {
      const res = await api.post("/recherche/stats/cohort/export", { job_id: result._jid, format }, { responseType: "blob" });
      blobDownload(res, `tableau-de-bord-cohorte.${format === "docx" ? "docx" : format === "xlsx" ? "xlsx" : "pdf"}`);
    } catch (err) { toast.error(apiError(err, "Export indisponible")); } finally { setExp(""); }
  };

  const sel = (label, key, opts, { testid, optional } = {}) => (
    <div>
      <label className="text-xs text-slate-500">{label}</label>
      <select value={mapping[key] || ""} onChange={(e) => setMap({ [key]: e.target.value })} data-testid={testid}
        className="mt-1 block w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-navy">
        {optional && <option value="">— Aucun —</option>}
        {opts.map((c) => <option key={c} value={c}>{c}</option>)}
      </select>
    </div>
  );

  const s = result?.summary || {};
  const bl = result?.blocks || {};

  return (
    <div className="space-y-5" data-testid="rs-cohort">
      <div className="rounded-2xl border border-slate-200 bg-white p-5">
        <h3 className="font-semibold text-navy">1. Importer les données de la cohorte</h3>
        <p className="mt-1 text-sm text-slate-500">Un seul fichier (Excel/CSV) contenant les sujets. Les colonnes clés sont détectées automatiquement, vous pouvez les ajuster.</p>
        <label className="mt-3 inline-flex cursor-pointer items-center gap-2 rounded-full bg-navy px-4 py-2 text-sm text-white hover:bg-navy-light" data-testid="rs-cohort-upload-label">
          {uploading ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />} Importer un fichier
          <input type="file" accept=".xlsx,.xls,.csv" className="hidden" onChange={upload} data-testid="rs-cohort-upload" />
        </label>
        {dataset && <span className="ml-3 text-sm text-emerald-600" data-testid="rs-cohort-file">{dataset.filename} · {dataset.n_rows} lignes</span>}
      </div>

      {dataset && (
        <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="rs-cohort-config">
          <h3 className="font-semibold text-navy">2. Mapper les colonnes</h3>
          <p className="mt-1 text-xs font-semibold uppercase tracking-wide text-[#0E7C7B]">Survie</p>
          <div className="mt-2 grid gap-3 sm:grid-cols-3">
            {sel("Durée / suivi (numérique) *", "duration", numCols, { testid: "rs-cohort-duration" })}
            {sel("Événement (0/1, 1 = décès) *", "event", allCols, { testid: "rs-cohort-event" })}
            {sel("Groupe (optionnel, Log-rank)", "group", groupCols, { testid: "rs-cohort-group", optional: true })}
          </div>
          <p className="mt-4 text-xs font-semibold uppercase tracking-wide text-[#0E7C7B]">Régression de Cox — covariables d'ajustement</p>
          <div className="mt-2 flex flex-wrap gap-2" data-testid="rs-cohort-covs">
            {allCols.filter((c) => c !== mapping.duration && c !== mapping.event).map((c) => (
              <label key={c} data-testid={`rs-cohort-cov-${c}`} className={`flex cursor-pointer items-center gap-1.5 rounded-full border px-3 py-1 text-xs transition ${mapping.covariates.includes(c) ? "border-[#0E7C7B] bg-[#0E7C7B]/10 text-navy" : "border-slate-200 text-slate-400"}`}>
                <input type="checkbox" checked={mapping.covariates.includes(c)} onChange={() => toggleCov(c)} className="accent-[#0E7C7B]" />{c}
              </label>
            ))}
          </div>
          <p className="mt-4 text-xs font-semibold uppercase tracking-wide text-[#0E7C7B]">Résumé & tendance temporelle (optionnels)</p>
          <div className="mt-2 grid gap-3 sm:grid-cols-4">
            {sel("Âge (numérique)", "age", numCols, { testid: "rs-cohort-age", optional: true })}
            {sel("Sexe", "sex", allCols, { testid: "rs-cohort-sex", optional: true })}
            {sel("Temps / date", "time", allCols, { testid: "rs-cohort-time", optional: true })}
            {sel("Valeur (numérique)", "value", numCols, { testid: "rs-cohort-value", optional: true })}
          </div>
          <div className="mt-4 flex flex-wrap items-end gap-4">
            <div>
              <label className="text-xs text-slate-500">Niveau de confiance</label>
              <select value={confidence} onChange={(e) => setConfidence(Number(e.target.value))} data-testid="rs-cohort-confidence" className="mt-1 block rounded-lg border border-slate-200 px-3 py-2 text-sm text-navy">
                <option value={0.90}>90 %</option><option value={0.95}>95 %</option><option value={0.99}>99 %</option>
              </select>
            </div>
            <div>
              <label className="text-xs text-slate-500">Horizon de prévision (pas)</label>
              <input type="number" min={1} max={60} value={horizon} onChange={(e) => setHorizon(Math.min(60, Math.max(1, Number(e.target.value) || 12)))} data-testid="rs-cohort-horizon" className="mt-1 block w-24 rounded-lg border border-slate-200 px-3 py-2 text-sm text-navy" />
            </div>
            <Button onClick={launch} disabled={running || !ready} data-testid="rs-cohort-run" className="rounded-full bg-navy text-sm text-white hover:bg-navy-light disabled:opacity-50">
              {running ? <Loader2 size={15} className="mr-1 animate-spin" /> : <Calculator size={15} className="mr-1" />} Générer le tableau de bord
            </Button>
          </div>
          {!ready && <p className="mt-1 text-xs text-amber-600">Sélectionnez au minimum la durée de suivi et l'événement (colonnes différentes).</p>}
        </div>
      )}

      {running && job && (
        <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="rs-cohort-progress">
          <p className="text-sm font-medium text-navy">{job.step || "Traitement"}…</p>
          <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-slate-100"><div className="h-full rounded-full bg-[#0E7C7B] transition-all" style={{ width: `${job.progress || 5}%` }} /></div>
        </div>
      )}

      {result && (
        <div className="space-y-5" data-testid="rs-cohort-result">
          <div className="rounded-2xl border border-[#0E7C7B]/30 bg-[#0E7C7B]/5 p-4" data-testid="rs-cohort-summary">
            <h3 className="mb-2 font-semibold text-navy">Résumé de la cohorte</h3>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <div className="rounded-xl bg-white p-3"><p className="text-xs text-slate-400">Effectif</p><p className="text-xl font-bold text-navy">{s.n}</p></div>
              {s.events != null && <div className="rounded-xl bg-white p-3"><p className="text-xs text-slate-400">Événements</p><p className="text-xl font-bold text-navy">{s.events} <span className="text-sm font-medium text-slate-500">({s.event_rate} %)</span></p></div>}
              {s.age && <div className="rounded-xl bg-white p-3"><p className="text-xs text-slate-400">Âge moyen</p><p className="text-xl font-bold text-navy">{s.age.mean} <span className="text-sm font-medium text-slate-500">± {s.age.std}</span></p></div>}
              {s.sex && <div className="rounded-xl bg-white p-3"><p className="text-xs text-slate-400">Sexe</p><p className="text-sm font-semibold text-navy">{s.sex.map((x) => `${x.value} ${x.pct}%`).join(" · ")}</p></div>}
            </div>
            {s.groups && <p className="mt-2 text-xs text-slate-500">Groupes : {s.groups.map((g) => `${g.label} (n=${g.n})`).join(" · ")}</p>}
          </div>

          {[["survival", "Survie (Kaplan-Meier & Log-rank)", "rs-cohort-survival"], ["cox", "Régression de Cox (survie ajustée)", "rs-cohort-cox"], ["timeseries", "Tendance temporelle & prévision", "rs-cohort-ts"]].map(([key, title, tid]) => {
            const r = bl[key];
            return (
              <div key={key} className="rounded-2xl border border-slate-200 bg-white p-5" data-testid={tid}>
                <h3 className="font-semibold text-navy">{title}</h3>
                {r ? (<>
                  <p className="mt-1 text-xs text-slate-500">{r.stat_name} {r.statistic != null ? `= ${r.statistic.toFixed(3)}` : ""}{r.p != null ? ` · p = ${r.p.toFixed(4)}` : ""} {r.decision && r.decision !== "—" ? `· ${r.decision}` : ""}</p>
                  <p className="mt-1 text-sm text-slate-600">{r.commentaire}</p>
                  {r.coefs?.length > 0 && (
                    <table className="mt-2 w-full text-left text-[11px]"><thead><tr className="text-slate-400">{["Covariable", "HR", "IC 95 %", "p"].map((h) => <th key={h} className="py-0.5 pr-2">{h}</th>)}</tr></thead>
                      <tbody>{r.coefs.map((c, k) => <tr key={k} className="border-t border-slate-100"><td className="py-0.5 pr-2 font-medium text-navy">{c.var}</td><td className="py-0.5 pr-2">{c.hr.toFixed(2)}</td><td className="py-0.5 pr-2">[{c.ci_low.toFixed(2)} ; {c.ci_high.toFixed(2)}]</td><td className="py-0.5 pr-2">{c.p < 0.05 ? <span className="font-semibold text-emerald-600">{c.p.toFixed(3)}</span> : c.p.toFixed(3)}</td></tr>)}</tbody>
                    </table>
                  )}
                  {r.medians?.length > 0 && (
                    <table className="mt-2 w-full text-left text-[11px]"><thead><tr className="text-slate-400">{["Groupe", "n", "Évén.", "Médiane survie"].map((h) => <th key={h} className="py-0.5 pr-2">{h}</th>)}</tr></thead>
                      <tbody>{r.medians.map((mm, k) => <tr key={k} className="border-t border-slate-100"><td className="py-0.5 pr-2 font-medium text-navy">{mm.group}</td><td className="py-0.5 pr-2">{mm.n}</td><td className="py-0.5 pr-2">{mm.events}</td><td className="py-0.5 pr-2">{mm.median != null ? mm.median.toFixed(1) : "non atteinte"}</td></tr>)}</tbody>
                    </table>
                  )}
                </>) : <p className="mt-1 text-sm text-slate-400">Non calculé (colonnes non renseignées).</p>}
              </div>
            );
          })}

          {result.charts?.length > 0 && (
            <div className="rounded-2xl border border-slate-200 bg-white p-5">
              <h3 className="mb-3 font-semibold text-navy">Graphiques</h3>
              <div className="grid gap-3 sm:grid-cols-2">
                {result.charts.map((c) => (
                  <figure key={c.id} className="rounded-xl border border-slate-100 p-2" data-testid={`rs-cohort-chart-${c.id}`}>
                    <img src={`data:image/png;base64,${c.png_b64}`} alt={c.title} className="w-full rounded" />
                    <figcaption className="mt-1 text-center text-xs text-slate-500">{c.title}</figcaption>
                  </figure>
                ))}
              </div>
            </div>
          )}

          {(aiStatus === "running" || ai) && (
            <div className="rounded-2xl border border-[#0E7C7B]/30 bg-white p-5" data-testid="rs-cohort-ai">
              <h3 className="mb-3 flex items-center gap-2 font-semibold text-navy"><Sparkles size={16} className="text-[#0E7C7B]" /> Analyse IA globale de la cohorte</h3>
              {aiStatus === "running" && <p className="flex items-center gap-2 text-sm text-slate-500" data-testid="rs-cohort-ai-loading"><Loader2 size={15} className="animate-spin text-[#0E7C7B]" /> Synthèse en cours (survie, Cox, tendance) et benchmarking littérature africaine puis universelle…</p>}
              {ai && (
                <div className="space-y-4">
                  <div data-testid="rs-cohort-ai-v1">
                    <p className="text-xs font-semibold uppercase tracking-wide text-[#0E7C7B]">Volet 1 — Indicateurs clés</p>
                    {ai.volet1?.synthese && <p className="mt-1 text-sm leading-relaxed text-slate-600">{ai.volet1.synthese}</p>}
                    {ai.volet1?.indicateurs?.length > 0 && (
                      <div className="mt-2 overflow-x-auto"><table className="w-full text-left text-xs"><thead><tr className="border-b border-slate-200 text-slate-500">{["Indicateur", "Valeur", "Lecture"].map((h) => <th key={h} className="px-2 py-1.5">{h}</th>)}</tr></thead>
                        <tbody>{ai.volet1.indicateurs.map((it, k) => <tr key={k} className="border-b border-slate-100 align-top"><td className="px-2 py-1.5 font-medium text-navy">{it.indicateur}</td><td className="px-2 py-1.5 text-[#0E7C7B]">{it.valeur}</td><td className="px-2 py-1.5 text-slate-600">{it.lecture}</td></tr>)}</tbody>
                      </table></div>
                    )}
                  </div>
                  <div data-testid="rs-cohort-ai-v2" className="space-y-2">
                    <p className="text-xs font-semibold uppercase tracking-wide text-[#0E7C7B]">Volet 2 — Interprétation & benchmarking</p>
                    {ai.volet2?.interpretation && <p className="text-sm leading-relaxed text-slate-600"><b className="text-navy">Interprétation. </b>{ai.volet2.interpretation}</p>}
                    {ai.volet2?.benchmarking && <p className="text-sm leading-relaxed text-slate-600"><b className="text-navy">Benchmarking (littérature africaine puis universelle). </b>{ai.volet2.benchmarking}</p>}
                    {ai.volet2?.limites && <p className="text-sm leading-relaxed text-slate-500"><b className="text-navy">Limites. </b>{ai.volet2.limites}</p>}
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="flex flex-wrap items-center gap-3 rounded-2xl border border-slate-200 bg-white p-4" data-testid="rs-cohort-actions">
            <Button onClick={runAi} disabled={aiStatus === "running"} data-testid="rs-cohort-ai-run" className="rounded-full bg-navy text-sm text-white hover:bg-navy-light">
              {aiStatus === "running" ? <Loader2 size={15} className="mr-1 animate-spin" /> : <Sparkles size={15} className="mr-1" />} Analyse IA
            </Button>
            <div className="h-6 w-px bg-slate-200" />
            <span className="text-xs font-medium text-slate-400">Télécharger :</span>
            <Button onClick={() => exportDoc("docx")} disabled={!!exp} data-testid="rs-cohort-export-docx" variant="outline" className="rounded-full border-[#0E7C7B] text-xs text-[#0E7C7B]">{exp === "docx" ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Download size={14} className="mr-1" />} Word</Button>
            <Button onClick={() => exportDoc("pdf")} disabled={!!exp} data-testid="rs-cohort-export-pdf" variant="outline" className="rounded-full border-[#0E7C7B] text-xs text-[#0E7C7B]">{exp === "pdf" ? <Loader2 size={14} className="mr-1 animate-spin" /> : <FileDown size={14} className="mr-1" />} PDF</Button>
            <Button onClick={() => exportDoc("xlsx")} disabled={!!exp} data-testid="rs-cohort-export-xlsx" variant="outline" className="rounded-full border-[#0E7C7B] text-xs text-[#0E7C7B]">{exp === "xlsx" ? <Loader2 size={14} className="mr-1 animate-spin" /> : <FileDown size={14} className="mr-1" />} Excel</Button>
          </div>
        </div>
      )}
    </div>
  );
}


function AnalyseStatTab() {
  const [view, setView] = useState("simple");
  const tab = (v, label) => (
    <button
      type="button"
      data-testid={`rs-analyse-view-${v}`}
      onClick={() => setView(v)}
      className={`rounded-full px-4 py-1.5 text-sm font-medium transition ${view === v ? "bg-navy text-white shadow" : "text-slate-500 hover:text-navy"}`}>
      {label}
    </button>
  );
  return (
    <div className="space-y-4" data-testid="rs-analyse-tab">
      <div className="inline-flex flex-wrap rounded-full border border-slate-200 bg-slate-50 p-1">
        {tab("simple", "Analyse d'une étude")}
        {tab("compare", "Comparaison d'études")}
        {tab("cohort", "Tableau de bord Cohorte")}
      </div>
      {view === "simple" ? <AnalyseStat /> : view === "compare" ? <CompareStudies /> : <CohortDashboard />}
    </div>
  );
}

function CompareStudies() {
  const [studies, setStudies] = useState([{ label: "Étude A", ds: null, up: false }, { label: "Étude B", ds: null, up: false }]);
  const [confidence, setConfidence] = useState(0.95);
  const [common, setCommon] = useState(null);
  const [selNum, setSelNum] = useState([]);
  const [selCat, setSelCat] = useState([]);
  const [numTest, setNumTest] = useState("auto");
  const [catTest, setCatTest] = useState("auto");
  const [job, setJob] = useState(null);
  const [result, setResult] = useState(null);
  const [running, setRunning] = useState(false);
  const [exp, setExp] = useState("");
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editName, setEditName] = useState("");
  const pollRef = useRef(null);
  useEffect(() => () => pollRef.current && clearInterval(pollRef.current), []);
  const [cAiStatus, setCAiStatus] = useState("idle");
  const [cAi, setCAi] = useState(null);
  const cAiPoll = useRef(null);
  useEffect(() => () => cAiPoll.current && clearInterval(cAiPoll.current), []);
  useEffect(() => { setCAiStatus("idle"); setCAi(null); }, [result?._jid]);
  const runCompareAi = async () => {
    if (!result?._jid) return;
    setCAiStatus("running"); setCAi(null);
    try {
      await api.post("/recherche/stats/ai-analysis", { job_id: result._jid });
      cAiPoll.current = setInterval(async () => {
        try {
          const { data } = await api.get(`/recherche/stats/ai-analysis/${result._jid}`);
          if (data.status === "done") { clearInterval(cAiPoll.current); setCAi(data.data); setCAiStatus("done"); }
          else if (data.status === "error") { clearInterval(cAiPoll.current); toast.error(data.error || "Analyse IA échouée"); setCAiStatus("idle"); }
        } catch { clearInterval(cAiPoll.current); setCAiStatus("idle"); }
      }, 3000);
    } catch (err) { toast.error(apiError(err, "Analyse IA indisponible")); setCAiStatus("idle"); }
  };

  const loadHistory = useCallback(async () => {
    try { const { data } = await api.get("/recherche/stats/compare/history"); setHistory(data || []); }
    catch { /* silencieux */ }
  }, []);
  useEffect(() => { loadHistory(); }, [loadHistory]);

  const reopen = async (id) => {
    try {
      const { data } = await api.get(`/recherche/stats/job/${id}`);
      if (data.status !== "done" || !data.result) { toast.error("Comparaison indisponible."); return; }
      setResult({ ...data.result, _jid: id });
      setShowHistory(false);
      toast.success("Comparaison rouverte");
    } catch (err) { toast.error(apiError(err, "Ouverture impossible")); }
  };

  const deleteHist = async (id, e) => {
    e.stopPropagation();
    try { await api.delete(`/recherche/stats/compare/history/${id}`); loadHistory(); if (result?._jid === id) setResult(null); }
    catch (err) { toast.error(apiError(err, "Suppression impossible")); }
  };

  const startRename = (h, e) => { e.stopPropagation(); setEditId(h.id); setEditName(h.name || (h.labels || []).join(" vs ")); };
  const saveRename = async (id, e) => {
    e && e.stopPropagation();
    try {
      await api.patch(`/recherche/stats/compare/history/${id}`, { name: editName });
      setEditId(null); loadHistory();
    } catch (err) { toast.error(apiError(err, "Renommage impossible")); }
  };

  const patch = (i, p) => setStudies((prev) => prev.map((s, k) => (k === i ? { ...s, ...p } : s)));
  const addStudy = () => setStudies((prev) => prev.length >= 6 ? prev : [...prev, { label: `Étude ${String.fromCharCode(65 + prev.length)}`, ds: null, up: false }]);
  const removeStudy = (i) => setStudies((prev) => prev.length <= 2 ? prev : prev.filter((_, k) => k !== i));

  const uploadOne = async (e, i) => {
    const file = e.target.files?.[0]; if (!file) return;
    patch(i, { up: true }); setResult(null); setJob(null);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/recherche/stats/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      patch(i, { ds: data });
    } catch (err) { toast.error(apiError(err, "Fichier non exploitable")); }
    finally { patch(i, { up: false }); e.target.value = ""; }
  };

  const readyCount = studies.filter((s) => s.ds).length;
  const readyIds = studies.filter((s) => s.ds).map((s) => s.ds.dataset_id);
  const idsKey = readyIds.join(",");
  useEffect(() => {
    if (readyIds.length < 2) { setCommon(null); return; }
    let cancel = false;
    (async () => {
      try {
        const { data } = await api.post("/recherche/stats/compare/common", { datasets: idsKey.split(",") });
        if (cancel) return;
        setCommon(data); setSelNum(data.numeric); setSelCat(data.categorical);
      } catch { if (!cancel) setCommon(null); }
    })();
    return () => { cancel = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idsKey]);
  const toggleSel = (arr, setArr, name) => setArr(arr.includes(name) ? arr.filter((x) => x !== name) : [...arr, name]);

  const launch = async () => {
    if (readyCount < 2) { toast.error("Importez au moins deux études."); return; }
    if (common && selNum.length + selCat.length === 0) { toast.error("Sélectionnez au moins une variable à comparer."); return; }
    setRunning(true); setResult(null); setJob({ progress: 5, step: "Initialisation" });
    try {
      const withData = studies.filter((s) => s.ds);
      const { data } = await api.post("/recherche/stats/compare", {
        datasets: withData.map((s) => s.ds.dataset_id), labels: withData.map((s) => s.label), confidence,
        include_numeric: common ? selNum : null, include_categorical: common ? selCat : null,
        num_test: numTest, cat_test: catTest });
      pollRef.current = setInterval(async () => {
        try {
          const { data: j } = await api.get(`/recherche/stats/job/${data.job_id}`);
          setJob(j);
          if (j.status === "done") { clearInterval(pollRef.current); setResult({ ...j.result, _jid: data.job_id }); setRunning(false); loadHistory(); }
          else if (j.status === "error") { clearInterval(pollRef.current); toast.error(j.error || "Comparaison échouée"); setRunning(false); }
        } catch { clearInterval(pollRef.current); setRunning(false); }
      }, 2500);
    } catch (err) { toast.error(apiError(err)); setRunning(false); }
  };

  const exportDoc = async (format) => {
    if (!result?._jid) return;
    setExp(format);
    try {
      const res = await api.post("/recherche/stats/compare/export", { job_id: result._jid, format }, { responseType: "blob" });
      blobDownload(res, format === "docx" ? "comparaison-etudes.docx" : "comparaison-etudes.pdf");
    } catch (err) { toast.error(apiError(err, "Export indisponible")); } finally { setExp(""); }
  };

  const fnum = (v, nd = 2) => (v === null || v === undefined || Number.isNaN(v)) ? "—" : Number(v).toFixed(nd);
  const nGroups = result?.meta?.n_groups || 0;

  return (
    <div className="space-y-5" data-testid="rs-compare">
      {history.length > 0 && (
        <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="rs-compare-history">
          <button type="button" onClick={() => setShowHistory((v) => !v)} data-testid="rs-compare-history-toggle" className="flex w-full items-center justify-between">
            <span className="flex items-center gap-2 font-semibold text-navy"><RefreshCw size={16} className="text-[#0E7C7B]" /> Comparaisons enregistrées ({history.length})</span>
            <span className="text-xs text-slate-400">{showHistory ? "Masquer" : "Afficher"}</span>
          </button>
          {showHistory && (
            <ul className="mt-3 space-y-2" data-testid="rs-compare-history-list">
              {history.map((h) => (
                <li key={h.id} data-testid={`rs-compare-history-${h.id}`} className="flex items-center justify-between gap-3 rounded-xl border border-slate-200 p-3 hover:bg-slate-50">
                  {editId === h.id ? (
                    <div className="flex min-w-0 flex-1 items-center gap-2">
                      <Input value={editName} onChange={(e) => setEditName(e.target.value)} autoFocus data-testid={`rs-compare-rename-input-${h.id}`} onKeyDown={(e) => e.key === "Enter" && saveRename(h.id, e)} className="h-8 bg-white text-sm" placeholder="Nom de la comparaison" />
                      <Button onClick={(e) => saveRename(h.id, e)} size="sm" data-testid={`rs-compare-rename-save-${h.id}`} className="h-8 rounded-full bg-[#0E7C7B] px-3 text-xs text-white"><Check size={14} /></Button>
                      <button type="button" onClick={(e) => { e.stopPropagation(); setEditId(null); }} className="rounded-full p-1.5 text-slate-400 hover:text-slate-600"><X size={15} /></button>
                    </div>
                  ) : (
                    <button type="button" onClick={() => reopen(h.id)} className="min-w-0 flex-1 text-left">
                      <p className="truncate text-sm font-medium text-navy">{h.name || (h.labels && h.labels.length ? h.labels.join(" vs ") : "Comparaison")}</p>
                      <p className="text-xs text-slate-500">{h.n_groups} études · {h.n_num} var. num. · {h.n_cat} var. qual.{h.created_at ? ` · ${new Date(h.created_at).toLocaleDateString("fr-FR")}` : ""}</p>
                    </button>
                  )}
                  {editId !== h.id && (
                    <div className="flex shrink-0 items-center gap-1">
                      <button type="button" onClick={(e) => startRename(h, e)} data-testid={`rs-compare-rename-${h.id}`} className="rounded-full p-1.5 text-slate-400 hover:bg-slate-100 hover:text-navy" title="Renommer"><Pencil size={14} /></button>
                      <Button onClick={() => reopen(h.id)} size="sm" variant="outline" data-testid={`rs-compare-reopen-${h.id}`} className="rounded-full text-xs">Rouvrir</Button>
                      <button type="button" onClick={(e) => deleteHist(h.id, e)} data-testid={`rs-compare-delete-${h.id}`} className="rounded-full p-1.5 text-slate-400 hover:bg-red-50 hover:text-red-500" title="Supprimer"><X size={15} /></button>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
      <div className="rounded-2xl border border-slate-200 bg-white p-5">
        <h3 className="font-semibold text-navy">1. Importer les études à comparer (2 à 6)</h3>
        <p className="mt-1 text-sm text-slate-500">Chargez plusieurs fichiers de données (Excel ou Word). La comparaison porte sur les variables portant le même nom dans tous les jeux. Le test s'adapte au nombre de groupes (2 : t de Welch / Mann-Whitney ; 3+ : ANOVA ou Kruskal-Wallis avec post-hoc).</p>
        <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {studies.map((s, i) => (
            <div key={i} className="rounded-xl border border-slate-200 bg-slate-50/60 p-4" data-testid={`rs-compare-zone-${i}`}>
              <div className="mb-3 flex items-center gap-2">
                <Input value={s.label} onChange={(e) => patch(i, { label: e.target.value })} data-testid={`rs-compare-label-${i}`} className="bg-white" placeholder={`Nom de l'étude ${i + 1}`} />
                {studies.length > 2 && (
                  <button type="button" onClick={() => removeStudy(i)} data-testid={`rs-compare-remove-${i}`} className="shrink-0 rounded-full p-1.5 text-slate-400 hover:bg-red-50 hover:text-red-500" title="Retirer"><X size={16} /></button>
                )}
              </div>
              <label className="flex cursor-pointer flex-col items-center justify-center gap-1 rounded-lg border-2 border-dashed border-slate-300 px-3 py-5 text-center text-xs text-slate-500 hover:bg-white" data-testid={`rs-compare-upload-label-${i}`}>
                {s.up ? <Loader2 size={18} className="animate-spin text-[#0E7C7B]" /> : <Upload size={18} className="text-[#0E7C7B]" />}
                <span className="font-medium">{s.ds ? "Remplacer le fichier" : "Choisir un fichier (.xlsx, .docx)"}</span>
                <input type="file" accept=".xlsx,.xls,.docx" onChange={(e) => uploadOne(e, i)} className="hidden" data-testid={`rs-compare-upload-${i}`} />
              </label>
              {s.ds && <p className="mt-2 text-xs text-navy" data-testid={`rs-compare-info-${i}`}><b>{s.ds.filename}</b> — {s.ds.n_rows} obs. · {s.ds.n_cols} var.</p>}
            </div>
          ))}
          {studies.length < 6 && (
            <button type="button" onClick={addStudy} data-testid="rs-compare-add" className="flex min-h-[140px] flex-col items-center justify-center gap-1 rounded-xl border-2 border-dashed border-slate-300 text-sm text-slate-500 transition hover:border-[#0E7C7B] hover:text-[#0E7C7B]">
              <Plus size={22} /> Ajouter une étude
            </button>
          )}
        </div>
      </div>

      {common && (common.numeric.length > 0 || common.categorical.length > 0) && (
        <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="rs-compare-config">
          <h3 className="font-semibold text-navy">2. Variables à comparer et test</h3>
          <p className="mt-1 text-sm text-slate-500">Variables communes détectées dans les {readyCount} études. Décochez celles à exclure.</p>
          {common.numeric.length > 0 && (
            <div className="mt-3">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Numériques</p>
              <div className="mt-2 flex flex-wrap gap-2" data-testid="rs-compare-sel-num">
                {common.numeric.map((v) => (
                  <label key={v} data-testid={`rs-compare-var-num-${v}`} className={`flex cursor-pointer items-center gap-1.5 rounded-full border px-3 py-1 text-xs transition ${selNum.includes(v) ? "border-[#0E7C7B] bg-[#0E7C7B]/10 text-navy" : "border-slate-200 text-slate-400"}`}>
                    <input type="checkbox" checked={selNum.includes(v)} onChange={() => toggleSel(selNum, setSelNum, v)} className="accent-[#0E7C7B]" />{v}
                  </label>
                ))}
              </div>
            </div>
          )}
          {common.categorical.length > 0 && (
            <div className="mt-3">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Qualitatives</p>
              <div className="mt-2 flex flex-wrap gap-2" data-testid="rs-compare-sel-cat">
                {common.categorical.map((v) => (
                  <label key={v} data-testid={`rs-compare-var-cat-${v}`} className={`flex cursor-pointer items-center gap-1.5 rounded-full border px-3 py-1 text-xs transition ${selCat.includes(v) ? "border-[#0E7C7B] bg-[#0E7C7B]/10 text-navy" : "border-slate-200 text-slate-400"}`}>
                    <input type="checkbox" checked={selCat.includes(v)} onChange={() => toggleSel(selCat, setSelCat, v)} className="accent-[#0E7C7B]" />{v}
                  </label>
                ))}
              </div>
            </div>
          )}
          <div className="mt-4 flex flex-wrap gap-5">
            <div>
              <label className="text-xs text-slate-500">Test (variables numériques)</label>
              <select value={numTest} onChange={(e) => setNumTest(e.target.value)} data-testid="rs-compare-num-test" className="mt-1 block rounded-lg border border-slate-200 px-3 py-2 text-sm text-navy">
                <option value="auto">Auto (selon la normalité)</option>
                <option value="parametric">Forcer paramétrique (t de Welch / ANOVA)</option>
                <option value="nonparametric">Forcer non-paramétrique (Mann-Whitney / Kruskal-Wallis)</option>
              </select>
            </div>
            <div>
              <label className="text-xs text-slate-500">Test (variables qualitatives)</label>
              <select value={catTest} onChange={(e) => setCatTest(e.target.value)} data-testid="rs-compare-cat-test" className="mt-1 block rounded-lg border border-slate-200 px-3 py-2 text-sm text-navy">
                <option value="auto">Khi² (par défaut)</option>
                <option value="fisher">Test exact de Fisher (tableaux 2×2)</option>
              </select>
            </div>
          </div>
        </div>
      )}
      {common && common.numeric.length === 0 && common.categorical.length === 0 && (
        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-700" data-testid="rs-compare-nocommon">
          Aucune variable commune détectée entre les études importées. Vérifiez que les fichiers partagent des colonnes de même nom.
        </div>
      )}

      <div className="rounded-2xl border border-slate-200 bg-white p-5">
        <h3 className="font-semibold text-navy">3. Lancer la comparaison</h3>
        <div className="mt-3 flex flex-wrap items-end gap-4">
          <div>
            <label className="text-xs text-slate-500">Niveau de confiance</label>
            <select value={confidence} onChange={(e) => setConfidence(Number(e.target.value))} data-testid="rs-compare-confidence" className="mt-1 block rounded-lg border border-slate-200 px-3 py-2 text-sm text-navy">
              <option value={0.90}>90 %</option>
              <option value={0.95}>95 %</option>
              <option value={0.99}>99 %</option>
            </select>
          </div>
          <Button onClick={launch} disabled={running || readyCount < 2} data-testid="rs-compare-run" className="rounded-full bg-navy text-sm text-white hover:bg-navy/90">
            {running ? <><Loader2 size={15} className="mr-1.5 animate-spin" /> Comparaison en cours…</> : <><BarChart3 size={15} className="mr-1.5" /> Comparer les études ({readyCount})</>}
          </Button>
        </div>
        {running && job && (
          <div className="mt-4" data-testid="rs-compare-progress">
            <div className="h-2 w-full overflow-hidden rounded-full bg-slate-100"><div className="h-full rounded-full bg-[#0E7C7B] transition-all" style={{ width: `${job.progress || 5}%` }} /></div>
            <p className="mt-1 text-xs text-slate-500">{job.step}</p>
          </div>
        )}
      </div>

      {result && (
        <div className="space-y-5" data-testid="rs-compare-result">
          <div className="flex flex-wrap items-center justify-between gap-2 rounded-2xl border border-slate-200 bg-white p-5">
            <div>
              <h3 className="font-semibold text-navy">Résultats — {nGroups === 2 ? "comparaison de 2 études" : `comparaison de ${nGroups} études (multicentrique)`}</h3>
              <p className="text-xs text-slate-500">{result.meta.groups.map((g) => `${g.label} (n=${g.n})`).join(" · ")} · confiance {Math.round(result.meta.confidence * 100)} %</p>
            </div>
            <div className="flex gap-2">
              <Button onClick={() => exportDoc("pdf")} disabled={!!exp} data-testid="rs-compare-export-pdf" variant="outline" className="rounded-full border-[#0E7C7B] text-xs text-[#0E7C7B]">{exp === "pdf" ? <Loader2 size={13} className="mr-1 animate-spin" /> : <Download size={13} className="mr-1" />} PDF</Button>
              <Button onClick={() => exportDoc("docx")} disabled={!!exp} data-testid="rs-compare-export-docx" variant="outline" className="rounded-full border-[#0E7C7B] text-xs text-[#0E7C7B]">{exp === "docx" ? <Loader2 size={13} className="mr-1 animate-spin" /> : <Download size={13} className="mr-1" />} Word</Button>
            </div>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-5">
            <h4 className="font-semibold text-navy">Variables quantitatives</h4>
            {result.numeric.length ? (
              <div className="mt-3 overflow-x-auto">
                <table className="w-full text-left text-xs" data-testid="rs-compare-num-table">
                  <thead><tr className="border-b border-slate-200 text-slate-500">
                    <th className="py-2 pr-3">Variable</th>
                    {result.meta.groups.map((g) => <th key={g.label} className="py-2 pr-3">{g.label}</th>)}
                    <th className="py-2 pr-3">Test</th>
                    <th className="py-2 pr-3">p</th>
                    <th className="py-2">Verdict</th>
                  </tr></thead>
                  <tbody>
                    {result.numeric.map((x) => (
                      <Fragment key={x.name}>
                        <tr className="border-b border-slate-100 align-top" data-testid={`rs-compare-num-${x.name}`}>
                          <td className="py-2 pr-3 font-medium text-navy">{x.name}{x.unit ? ` (${x.unit})` : ""}</td>
                          {x.groups.map((g) => <td key={g.label} className="py-2 pr-3 text-slate-600">{fnum(g.mean)}±{fnum(g.std)} <span className="text-slate-400">(n={g.n})</span></td>)}
                          <td className="py-2 pr-3 text-slate-600">{x.test}</td>
                          <td className="py-2 pr-3 text-slate-600">{x.p === null || x.p === undefined ? "—" : fnum(x.p, 4)}</td>
                          <td className="py-2">
                            <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ${x.significant ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-500"}`}>
                              {x.significant ? <AlertTriangle size={11} /> : <CheckCircle2 size={11} />} {x.decision}
                            </span>
                          </td>
                        </tr>
                        {x.posthoc && x.posthoc.length > 0 && (
                          <tr className="border-b border-slate-100" data-testid={`rs-compare-posthoc-${x.name}`}>
                            <td colSpan={result.meta.groups.length + 4} className="pb-2 pl-3 text-[11px] text-slate-500">
                              <span className="font-medium text-slate-600">Post-hoc :</span> {x.posthoc.map((p, k) => (
                                <span key={k} className={`ml-2 ${p.significant ? "text-amber-700" : "text-slate-400"}`}>{p.a} vs {p.b} (p={fnum(p.p, 4)}{p.significant ? " *" : ""})</span>
                              ))}
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : <p className="mt-2 text-sm text-slate-500">Aucune variable quantitative commune aux études.</p>}
          </div>

          {result.categorical.length > 0 && (
            <div className="rounded-2xl border border-slate-200 bg-white p-5">
              <h4 className="font-semibold text-navy">Variables qualitatives</h4>
              <div className="mt-3 grid gap-3 sm:grid-cols-2" data-testid="rs-compare-cat">
                {result.categorical.map((x) => (
                  <div key={x.name} className="rounded-xl border border-slate-200 p-3">
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-navy">{x.name}</span>
                      <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${x.significant ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-500"}`}>{x.decision}</span>
                    </div>
                    <p className="mt-1 text-xs text-slate-500">{x.test || "Khi²"} · p = {fnum(x.p, 4)} · Khi² = {fnum(x.chi2, 3)}{x.cramers_v !== null && x.cramers_v !== undefined ? ` · V de Cramér = ${fnum(x.cramers_v, 3)}` : ""}</p>
                    {x.dist.map((gd) => (
                      <p key={gd.label} className="mt-1.5 text-xs text-slate-600"><b>{gd.label} :</b> {gd.values.map((d) => `${d.value} ${d.pct}%`).join(", ")}</p>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          )}

          {result.charts.length > 0 && (
            <div className="rounded-2xl border border-slate-200 bg-white p-5">
              <h4 className="font-semibold text-navy">Graphiques comparatifs</h4>
              <div className="mt-3 grid gap-4 sm:grid-cols-2" data-testid="rs-compare-charts">
                {result.charts.map((c, i) => (
                  <figure key={i}><img src={`data:image/png;base64,${c.png_b64}`} alt={c.title} className="w-full rounded" /><figcaption className="mt-1 text-center text-xs text-slate-500">{c.title}</figcaption></figure>
                ))}
              </div>
            </div>
          )}

          {result.synthese && (
            <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="rs-compare-synthese">
              <h4 className="flex items-center gap-1.5 font-semibold text-navy"><Sparkles size={15} className="text-[#0E7C7B]" /> Synthèse comparative (IA)</h4>
              <div className="mt-2 space-y-2 whitespace-pre-wrap text-sm leading-relaxed text-slate-700">{result.synthese}</div>
            </div>
          )}

          {(cAiStatus === "running" || cAi) && (
            <div className="rounded-2xl border border-[#0E7C7B]/30 bg-white p-5" data-testid="rs-compare-ai">
              <h4 className="mb-3 flex items-center gap-2 font-semibold text-navy"><Sparkles size={16} className="text-[#0E7C7B]" /> Analyse IA structurée</h4>
              {cAiStatus === "running" && <p className="flex items-center gap-2 text-sm text-slate-500" data-testid="rs-compare-ai-loading"><Loader2 size={15} className="animate-spin text-[#0E7C7B]" /> L'IA compare les études et confronte à la littérature (africaine puis universelle)…</p>}
              {cAi && (
                <div className="space-y-4">
                  <div data-testid="rs-compare-ai-v1">
                    <p className="text-xs font-semibold uppercase tracking-wide text-[#0E7C7B]">Volet 1 — Indicateurs comparés</p>
                    {cAi.volet1?.synthese && <p className="mt-1 text-sm leading-relaxed text-slate-600">{cAi.volet1.synthese}</p>}
                    {cAi.volet1?.indicateurs?.length > 0 && (
                      <div className="mt-2 overflow-x-auto">
                        <table className="w-full text-left text-xs">
                          <thead><tr className="border-b border-slate-200 text-slate-500">{["Indicateur", "Valeur", "Lecture"].map((h) => <th key={h} className="px-2 py-1.5">{h}</th>)}</tr></thead>
                          <tbody>
                            {cAi.volet1.indicateurs.map((it, k) => (
                              <tr key={k} className="border-b border-slate-100 align-top">
                                <td className="px-2 py-1.5 font-medium text-navy">{it.indicateur}</td>
                                <td className="px-2 py-1.5 text-[#0E7C7B]">{it.valeur}</td>
                                <td className="px-2 py-1.5 text-slate-600">{it.lecture}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                  <div data-testid="rs-compare-ai-v2" className="space-y-2">
                    <p className="text-xs font-semibold uppercase tracking-wide text-[#0E7C7B]">Volet 2 — Interprétation & benchmarking</p>
                    {cAi.volet2?.interpretation && <p className="text-sm leading-relaxed text-slate-600"><b className="text-navy">Interprétation. </b>{cAi.volet2.interpretation}</p>}
                    {cAi.volet2?.benchmarking && <p className="text-sm leading-relaxed text-slate-600"><b className="text-navy">Benchmarking (littérature africaine puis universelle). </b>{cAi.volet2.benchmarking}</p>}
                    {cAi.volet2?.limites && <p className="text-sm leading-relaxed text-slate-500"><b className="text-navy">Limites. </b>{cAi.volet2.limites}</p>}
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="flex flex-wrap items-center gap-3 rounded-2xl border border-slate-200 bg-white p-4" data-testid="rs-compare-actions">
            <Button onClick={runCompareAi} disabled={cAiStatus === "running"} data-testid="rs-compare-ai-run" className="rounded-full bg-navy text-sm text-white hover:bg-navy-light">
              {cAiStatus === "running" ? <Loader2 size={15} className="mr-1 animate-spin" /> : <Sparkles size={15} className="mr-1" />} Analyse IA
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}


function AnalyseStat() {
  const [dataset, setDataset] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [confidence, setConfidence] = useState(0.95);
  const [mode, setMode] = useState("auto");
  const [test, setTest] = useState("ttest");
  const [v1, setV1] = useState("");
  const [v2, setV2] = useState("");
  const [v3, setV3] = useState("");
  const [pairCols, setPairCols] = useState([]);
  const [coxCovs, setCoxCovs] = useState([]);
  const [horizon, setHorizon] = useState(12);
  const [job, setJob] = useState(null);
  const [result, setResult] = useState(null);
  const [running, setRunning] = useState(false);
  const [exp, setExp] = useState("");
  const [colCfg, setColCfg] = useState([]);
  const [missing, setMissing] = useState("keep");
  const [cleaning, setCleaning] = useState(false);
  const [tpl, setTpl] = useState(false);
  const [flagAnom, setFlagAnom] = useState(true);
  const [boundsCfg, setBoundsCfg] = useState({});
  const pollRef = useRef(null);

  useEffect(() => () => pollRef.current && clearInterval(pollRef.current), []);
  useEffect(() => {
    if (dataset) {
      setColCfg(dataset.columns.map((c) => ({ name: c.name, type: c.type, unit: c.unit || "", include: true })));
      const bc = {};
      (dataset.bounds || []).forEach((b) => { bc[b.column] = { min: b.min, max: b.max }; });
      setBoundsCfg(bc);
    }
  }, [dataset]);

  const downloadTemplate = async () => {
    setTpl(true);
    try {
      const res = await api.get("/recherche/stats/template", { responseType: "blob" });
      blobDownload(res, "modele-donnees-caris.xlsx");
    } catch (err) { toast.error(apiError(err, "Modèle indisponible")); } finally { setTpl(false); }
  };

  const applyClean = async () => {
    if (!dataset) return;
    setCleaning(true); setResult(null); setJob(null);
    try {
      const { data } = await api.post("/recherche/stats/clean", {
        dataset_id: dataset.dataset_id, columns: colCfg, missing_strategy: missing, flag_anomalies: flagAnom,
        custom_bounds: boundsCfg,
      });
      setDataset(data);
      const fixed = data.anomalies_fixed ? ` · ${data.anomalies_fixed} valeur(s) aberrante(s) neutralisée(s)` : "";
      toast.success(`Données nettoyées — ${data.n_rows} observations, ${data.n_cols} variables${fixed}`);
    } catch (err) { toast.error(apiError(err, "Nettoyage impossible")); } finally { setCleaning(false); }
  };

  const setCol = (i, patch) => setColCfg((p) => p.map((c, idx) => idx === i ? { ...c, ...patch } : c));
  const setBound = (col, patch) => setBoundsCfg((p) => ({ ...p, [col]: { ...(p[col] || {}), ...patch } }));

  const upload = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setUploading(true); setResult(null); setJob(null);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/recherche/stats/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setDataset(data);
      const cols = data.columns.map((c) => c.name);
      setV1(cols[0] || ""); setV2(cols[1] || "");
    } catch (err) { toast.error(apiError(err, "Fichier non exploitable")); } finally { setUploading(false); e.target.value = ""; }
  };

  const launch = async () => {
    if (!dataset) return;
    setRunning(true); setResult(null); setJob({ progress: 5, step: "Initialisation" });
    try {
      const body = { dataset_id: dataset.dataset_id, confidence, mode };
      if (mode === "manual") { body.test = test; body.variables = manualVars(); if (testMode === "timeseries") body.horizon = horizon; }
      const { data } = await api.post("/recherche/stats/analyze", body);
      pollRef.current = setInterval(async () => {
        try {
          const { data: j } = await api.get(`/recherche/stats/job/${data.job_id}`);
          setJob(j);
          if (j.status === "done") { clearInterval(pollRef.current); setResult({ ...j.result, _jid: data.job_id }); setRunning(false); }
          else if (j.status === "error") { clearInterval(pollRef.current); toast.error(j.error || "Analyse échouée"); setRunning(false); }
        } catch { clearInterval(pollRef.current); setRunning(false); }
      }, 2500);
    } catch (err) { toast.error(apiError(err)); setRunning(false); }
  };

  const exportDoc = async (format) => {
    if (!result?._jid) return;
    setExp(format);
    try {
      const res = await api.post("/recherche/stats/export", { job_id: result._jid, format }, { responseType: "blob" });
      const fname = format === "docx" ? "analyse-statistique.docx" : (format === "xlsx" ? "analyse-statistique.xlsx" : "analyse-statistique.pdf");
      blobDownload(res, fname);
    } catch (err) { toast.error(apiError(err, "Export indisponible")); } finally { setExp(""); }
  };

  const cols = dataset?.columns || [];
  const allCols = cols.map((c) => c.name);
  const numCols = cols.filter((c) => c.type === "numeric").map((c) => c.name);
  const catCols = cols.filter((c) => c.type === "categorical").map((c) => c.name);
  // Colonnes de groupe : qualitatives à faible cardinalité (2 à 12 modalités), sinon repli
  const groupCols = (() => {
    const g = cols.filter((c) => c.type === "categorical" && (c.unique == null || (c.unique >= 2 && c.unique <= 12))).map((c) => c.name);
    return g.length ? g : catCols;
  })();
  const testMode = TEST_MODE[test] || "";
  const isPaired = testMode === "paired2" || testMode === "pairedN";

  // Réinitialise les variables sélectionnées quand le test ou le jeu de données change
  useEffect(() => {
    if (testMode === "num+group") { setV1(numCols[0] || ""); setV2(groupCols[0] || ""); }
    else if (testMode === "num+num") { setV1(numCols[0] || ""); setV2(numCols[1] || numCols[0] || ""); }
    else if (testMode === "cat+cat" || testMode === "catpair2") { setV1(groupCols[0] || ""); setV2(groupCols[1] || groupCols[0] || ""); }
    else if (testMode === "cat1") { setV1(groupCols[0] || ""); }
    else if (testMode === "survival") { setV1(numCols[0] || ""); setV2(allCols.find((c) => c !== numCols[0]) || ""); setV3(""); }
    else if (testMode === "cox") { setV1(numCols[0] || ""); setV2(allCols.find((c) => c !== numCols[0]) || ""); setCoxCovs([]); }
    else if (testMode === "timeseries") { setV1(allCols[0] || ""); setV2(numCols.find((c) => c !== allCols[0]) || numCols[0] || ""); }
    else if (isPaired) { setPairCols(numCols.slice(0, testMode === "paired2" ? 2 : 3)); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [test, dataset]);

  const manualVars = () => {
    if (testMode === "cat1") return [v1].filter(Boolean);
    if (testMode === "survival") return [v1, v2, ...(v3 ? [v3] : [])].filter(Boolean);
    if (testMode === "cox") return [v1, v2, ...coxCovs].filter(Boolean);
    if (testMode === "timeseries") return [v1, v2].filter(Boolean);
    if (isPaired) return pairCols;
    if (["num+group", "num+num", "cat+cat", "catpair2"].includes(testMode)) return [v1, v2].filter(Boolean);
    return [];
  };
  const togglePair = (name) => setPairCols((p) => p.includes(name) ? p.filter((x) => x !== name) : [...p, name]);
  const toggleCov = (name) => setCoxCovs((p) => p.includes(name) ? p.filter((x) => x !== name) : [...p, name]);
  const pickTest = (v) => { if (v) setTest(v); };

  const manualReady = (() => {
    if (mode !== "manual") return true;
    if (testMode === "cat1") return !!v1;
    if (testMode === "survival") return !!v1 && !!v2 && v1 !== v2;
    if (testMode === "cox") return !!v1 && !!v2 && v1 !== v2 && coxCovs.length >= 1;
    if (testMode === "timeseries") return !!v1 && !!v2 && v1 !== v2;
    if (testMode === "paired2") return pairCols.length === 2;
    if (testMode === "pairedN") return pairCols.length >= 3;
    if (["num*", "auto"].includes(testMode)) return true;
    return !!v1 && !!v2 && v1 !== v2;
  })();


  return (
    <div className="space-y-5" data-testid="rs-analyse">
      <div className="rounded-2xl border border-slate-200 bg-white p-5">
        <h3 className="font-semibold text-navy">1. Importer vos données</h3>
        <p className="mt-1 text-sm text-slate-500">Fichier Excel (.xlsx) ou Word (.docx) contenant un tableau de données cliniques, épidémiologiques ou biologiques.</p>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <label className="inline-flex cursor-pointer items-center gap-2 rounded-full bg-[#0E7C7B] px-4 py-2 text-sm font-medium text-white hover:bg-[#0b6463]" data-testid="rs-stat-upload-label">
            {uploading ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />} Choisir un fichier
            <input type="file" accept=".xlsx,.xls,.docx" onChange={upload} className="hidden" data-testid="rs-stat-upload" />
          </label>
          <Button onClick={downloadTemplate} disabled={tpl} data-testid="rs-stat-template" variant="outline" className="rounded-full border-slate-300 text-sm text-slate-600">
            {tpl ? <Loader2 size={15} className="mr-1 animate-spin" /> : <FileDown size={15} className="mr-1" />} Télécharger le modèle Excel
          </Button>
        </div>
        {dataset && (
          <div className="mt-4" data-testid="rs-stat-preview">
            <p className="text-sm text-navy"><b>{dataset.filename}</b> — {dataset.n_rows} observations · {dataset.n_cols} variables</p>
          </div>
        )}
      </div>

      {dataset && (
        <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="rs-stat-clean">
          <h3 className="font-semibold text-navy">2. Vérifier & nettoyer les données</h3>
          <p className="mt-1 text-sm text-slate-500">Corrigez le type et l'unité de chaque variable, excluez les colonnes inutiles, puis choisissez le traitement des valeurs manquantes.</p>
          <div className="mt-3 overflow-x-auto">
            <table className="w-full text-left text-xs">
              <thead><tr className="border-b border-slate-200 text-slate-500">
                <th className="px-2 py-1.5">Conserver</th><th className="px-2 py-1.5">Variable</th>
                <th className="px-2 py-1.5">Type</th><th className="px-2 py-1.5">Unité</th><th className="px-2 py-1.5">Manquantes</th>
              </tr></thead>
              <tbody>
                {colCfg.map((c, i) => {
                  const meta = dataset.columns.find((m) => m.name === c.name) || {};
                  return (
                    <tr key={c.name} className="border-b border-slate-100" data-testid={`rs-clean-row-${i}`}>
                      <td className="px-2 py-1.5"><input type="checkbox" checked={c.include} onChange={(e) => setCol(i, { include: e.target.checked })} data-testid={`rs-clean-include-${i}`} className="h-4 w-4 rounded border-slate-300 text-[#0E7C7B]" /></td>
                      <td className="px-2 py-1.5 font-medium text-navy">{c.name}</td>
                      <td className="px-2 py-1.5">
                        <select value={c.type} onChange={(e) => setCol(i, { type: e.target.value })} data-testid={`rs-clean-type-${i}`} className="rounded-lg border border-slate-200 px-2 py-1 text-xs text-navy">
                          <option value="numeric">Quantitative</option><option value="categorical">Qualitative</option>
                        </select>
                      </td>
                      <td className="px-2 py-1.5"><input value={c.unit} onChange={(e) => setCol(i, { unit: e.target.value })} placeholder="ex. mmHg" data-testid={`rs-clean-unit-${i}`} className="w-24 rounded-lg border border-slate-200 px-2 py-1 text-xs text-navy" /></td>
                      <td className="px-2 py-1.5 text-slate-400">{meta.missing || 0}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="mt-4 flex flex-wrap items-end gap-4">
            <div>
              <label className="text-xs text-slate-500">Valeurs manquantes</label>
              <select value={missing} onChange={(e) => setMissing(e.target.value)} data-testid="rs-clean-missing" className="mt-1 block rounded-lg border border-slate-200 px-3 py-2 text-sm text-navy">
                <option value="keep">Laisser telles quelles</option>
                <option value="drop">Supprimer les lignes incomplètes</option>
                <option value="impute">Remplacer (moyenne / mode)</option>
              </select>
            </div>
            <Button onClick={applyClean} disabled={cleaning} data-testid="rs-clean-apply" variant="outline" className="rounded-full border-[#0E7C7B] text-sm text-[#0E7C7B]">
              {cleaning ? <Loader2 size={15} className="mr-1 animate-spin" /> : <RefreshCw size={15} className="mr-1" />} Appliquer le nettoyage
            </Button>
            <span className="text-xs text-slate-400">{colCfg.filter((c) => c.include).length} variable(s) conservée(s)</span>
          </div>
          {dataset.bounds?.length > 0 && (
            <div className="mt-4 rounded-xl border border-amber-300 bg-amber-50 p-4" data-testid="rs-anomalies">
              <p className="flex items-center gap-2 text-sm font-semibold text-amber-800"><AlertTriangle size={16} /> Bornes cliniques plausibles (ajustables)</p>
              <p className="mt-1 text-xs text-amber-800">Ajustez au besoin les bornes min/max de chaque variable ; les valeurs en dehors sont comptées comme aberrantes. Cliquez ensuite sur « Appliquer le nettoyage » pour recompter (et neutraliser si l'option ci-dessous est cochée).</p>
              <div className="mt-2 overflow-x-auto">
                <table className="w-full text-left text-xs">
                  <thead><tr className="border-b border-amber-200 text-amber-800">
                    <th className="px-2 py-1">Variable</th><th className="px-2 py-1">Min</th><th className="px-2 py-1">Max</th><th className="px-2 py-1">Unité</th><th className="px-2 py-1">Hors bornes</th>
                  </tr></thead>
                  <tbody>
                    {dataset.bounds.map((b) => {
                      const bc = boundsCfg[b.column] || { min: b.min, max: b.max };
                      return (
                        <tr key={b.column} className="border-b border-amber-100" data-testid={`rs-anomaly-${b.column}`}>
                          <td className="px-2 py-1 font-medium text-amber-900">{b.column} <span className="text-amber-600">({b.label})</span></td>
                          <td className="px-2 py-1"><input type="number" value={bc.min} onChange={(e) => setBound(b.column, { min: e.target.value })} data-testid={`rs-bound-min-${b.column}`} className="w-20 rounded border border-amber-300 bg-white px-1.5 py-0.5 text-xs text-navy" /></td>
                          <td className="px-2 py-1"><input type="number" value={bc.max} onChange={(e) => setBound(b.column, { max: e.target.value })} data-testid={`rs-bound-max-${b.column}`} className="w-20 rounded border border-amber-300 bg-white px-1.5 py-0.5 text-xs text-navy" /></td>
                          <td className="px-2 py-1 text-amber-700">{b.unit || "—"}</td>
                          <td className="px-2 py-1"><span className={b.n_outliers > 0 ? "font-semibold text-red-600" : "text-amber-700"}>{b.n_outliers}</span></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <label className="mt-3 flex items-center gap-2 text-xs font-medium text-amber-900" data-testid="rs-anomaly-flag-label">
                <input type="checkbox" checked={flagAnom} onChange={(e) => setFlagAnom(e.target.checked)} data-testid="rs-anomaly-flag" className="h-4 w-4 rounded border-amber-400 text-amber-600" />
                Neutraliser les valeurs hors bornes (les traiter comme manquantes) lors du nettoyage
              </label>
            </div>
          )}
        </div>
      )}

      {dataset && (
        <div className="rounded-2xl border border-slate-200 bg-white p-5">
          <h3 className="font-semibold text-navy">3. Paramètres d'analyse</h3>
          <div className="mt-3 flex flex-wrap items-end gap-4">
            <div>
              <label className="text-xs text-slate-500">Niveau de confiance</label>
              <select value={confidence} onChange={(e) => setConfidence(Number(e.target.value))} data-testid="rs-stat-confidence" className="mt-1 block rounded-lg border border-slate-200 px-3 py-2 text-sm text-navy">
                <option value={0.90}>90 %</option><option value={0.95}>95 %</option><option value={0.99}>99 %</option>
              </select>
            </div>
            <div>
              <label className="text-xs text-slate-500">Mode</label>
              <select value={mode} onChange={(e) => setMode(e.target.value)} data-testid="rs-stat-mode" className="mt-1 block rounded-lg border border-slate-200 px-3 py-2 text-sm text-navy">
                <option value="auto">Automatique (tests adaptés)</option>
                <option value="manual">Manuel (choisir un test)</option>
              </select>
            </div>
            {mode === "auto" && (
              <Button onClick={launch} disabled={running} data-testid="rs-stat-run" className="rounded-full bg-navy text-sm text-white hover:bg-navy/90">
                {running ? <Loader2 size={15} className="mr-1 animate-spin" /> : <Calculator size={15} className="mr-1" />} Lancer l'analyse
              </Button>
            )}
          </div>

          {mode === "manual" && (
            <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50/60 p-4" data-testid="rs-stat-manual">
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">Choisissez un test dans l'une des trois familles</p>
              <div className="grid gap-3 sm:grid-cols-3">
                {TEST_FAMILIES.map((fam) => (
                  <div key={fam.key}>
                    <label className="text-xs font-medium text-navy">{fam.label}</label>
                    <select value={familyOf(test) === fam.key ? test : ""} onChange={(e) => pickTest(e.target.value)}
                      data-testid={`rs-stat-test-${fam.key}`} className={`mt-1 block w-full rounded-lg border px-3 py-2 text-sm text-navy ${familyOf(test) === fam.key ? "border-[#0E7C7B] ring-1 ring-[#0E7C7B]/30" : "border-slate-200"}`}>
                      <option value="">— Choisir un test —</option>
                      {fam.tests.map((t) => <option key={t.v} value={t.v}>{t.l}</option>)}
                    </select>
                  </div>
                ))}
              </div>

              <div className="mt-4 flex flex-wrap items-end gap-4" data-testid="rs-stat-vars">
                {testMode === "num+group" && (<>
                  <div>
                    <label className="text-xs text-slate-500">Variable numérique</label>
                    <select value={v1} onChange={(e) => setV1(e.target.value)} data-testid="rs-stat-v1" className="mt-1 block rounded-lg border border-slate-200 px-3 py-2 text-sm text-navy">{numCols.map((c) => <option key={c} value={c}>{c}</option>)}</select>
                  </div>
                  <div>
                    <label className="text-xs text-slate-500">Variable de groupe</label>
                    <select value={v2} onChange={(e) => setV2(e.target.value)} data-testid="rs-stat-v2" className="mt-1 block rounded-lg border border-slate-200 px-3 py-2 text-sm text-navy">{groupCols.map((c) => <option key={c} value={c}>{c}</option>)}</select>
                  </div>
                </>)}
                {testMode === "num+num" && (<>
                  <div>
                    <label className="text-xs text-slate-500">Variable X</label>
                    <select value={v1} onChange={(e) => setV1(e.target.value)} data-testid="rs-stat-v1" className="mt-1 block rounded-lg border border-slate-200 px-3 py-2 text-sm text-navy">{numCols.map((c) => <option key={c} value={c}>{c}</option>)}</select>
                  </div>
                  <div>
                    <label className="text-xs text-slate-500">Variable Y</label>
                    <select value={v2} onChange={(e) => setV2(e.target.value)} data-testid="rs-stat-v2" className="mt-1 block rounded-lg border border-slate-200 px-3 py-2 text-sm text-navy">{numCols.map((c) => <option key={c} value={c}>{c}</option>)}</select>
                  </div>
                </>)}
                {(testMode === "cat+cat" || testMode === "catpair2") && (<>
                  <div>
                    <label className="text-xs text-slate-500">{testMode === "catpair2" ? "Mesure 1 (avant)" : "Variable 1"}</label>
                    <select value={v1} onChange={(e) => setV1(e.target.value)} data-testid="rs-stat-v1" className="mt-1 block rounded-lg border border-slate-200 px-3 py-2 text-sm text-navy">{groupCols.map((c) => <option key={c} value={c}>{c}</option>)}</select>
                  </div>
                  <div>
                    <label className="text-xs text-slate-500">{testMode === "catpair2" ? "Mesure 2 (après)" : "Variable 2"}</label>
                    <select value={v2} onChange={(e) => setV2(e.target.value)} data-testid="rs-stat-v2" className="mt-1 block rounded-lg border border-slate-200 px-3 py-2 text-sm text-navy">{groupCols.map((c) => <option key={c} value={c}>{c}</option>)}</select>
                  </div>
                </>)}
                {testMode === "cat1" && (
                  <div>
                    <label className="text-xs text-slate-500">Variable qualitative</label>
                    <select value={v1} onChange={(e) => setV1(e.target.value)} data-testid="rs-stat-v1" className="mt-1 block rounded-lg border border-slate-200 px-3 py-2 text-sm text-navy">{groupCols.map((c) => <option key={c} value={c}>{c}</option>)}</select>
                  </div>
                )}
                {testMode === "survival" && (<>
                  <div>
                    <label className="text-xs text-slate-500">Durée / suivi (numérique)</label>
                    <select value={v1} onChange={(e) => setV1(e.target.value)} data-testid="rs-stat-v1" className="mt-1 block rounded-lg border border-slate-200 px-3 py-2 text-sm text-navy">{numCols.map((c) => <option key={c} value={c}>{c}</option>)}</select>
                  </div>
                  <div>
                    <label className="text-xs text-slate-500">Événement (0/1, 1 = décès/événement)</label>
                    <select value={v2} onChange={(e) => setV2(e.target.value)} data-testid="rs-stat-v2" className="mt-1 block rounded-lg border border-slate-200 px-3 py-2 text-sm text-navy">{allCols.map((c) => <option key={c} value={c}>{c}</option>)}</select>
                  </div>
                  <div>
                    <label className="text-xs text-slate-500">Groupe (optionnel, pour le Log-rank)</label>
                    <select value={v3} onChange={(e) => setV3(e.target.value)} data-testid="rs-stat-v3" className="mt-1 block rounded-lg border border-slate-200 px-3 py-2 text-sm text-navy"><option value="">— Aucun —</option>{groupCols.map((c) => <option key={c} value={c}>{c}</option>)}</select>
                  </div>
                </>)}
                {testMode === "timeseries" && (<>
                  <div>
                    <label className="text-xs text-slate-500">Temps / date</label>
                    <select value={v1} onChange={(e) => setV1(e.target.value)} data-testid="rs-stat-v1" className="mt-1 block rounded-lg border border-slate-200 px-3 py-2 text-sm text-navy">{allCols.map((c) => <option key={c} value={c}>{c}</option>)}</select>
                  </div>
                  <div>
                    <label className="text-xs text-slate-500">Valeur (numérique)</label>
                    <select value={v2} onChange={(e) => setV2(e.target.value)} data-testid="rs-stat-v2" className="mt-1 block rounded-lg border border-slate-200 px-3 py-2 text-sm text-navy">{numCols.map((c) => <option key={c} value={c}>{c}</option>)}</select>
                  </div>
                  <div>
                    <label className="text-xs text-slate-500">Horizon de prévision (pas)</label>
                    <input type="number" min={1} max={60} value={horizon} onChange={(e) => setHorizon(Math.min(60, Math.max(1, Number(e.target.value) || 12)))} data-testid="rs-stat-horizon" className="mt-1 block w-24 rounded-lg border border-slate-200 px-3 py-2 text-sm text-navy" />
                  </div>
                </>)}
                {testMode === "cox" && (<>
                  <div>
                    <label className="text-xs text-slate-500">Durée / suivi (numérique)</label>
                    <select value={v1} onChange={(e) => setV1(e.target.value)} data-testid="rs-stat-v1" className="mt-1 block rounded-lg border border-slate-200 px-3 py-2 text-sm text-navy">{numCols.map((c) => <option key={c} value={c}>{c}</option>)}</select>
                  </div>
                  <div>
                    <label className="text-xs text-slate-500">Événement (0/1, 1 = décès/événement)</label>
                    <select value={v2} onChange={(e) => setV2(e.target.value)} data-testid="rs-stat-v2" className="mt-1 block rounded-lg border border-slate-200 px-3 py-2 text-sm text-navy">{allCols.map((c) => <option key={c} value={c}>{c}</option>)}</select>
                  </div>
                  <div className="w-full">
                    <label className="text-xs text-slate-500">Covariables d'ajustement (numériques et/ou qualitatives) — sélectionnez-en au moins une</label>
                    <div className="mt-2 flex flex-wrap gap-2" data-testid="rs-stat-cox-covs">
                      {allCols.filter((c) => c !== v1 && c !== v2).map((c) => (
                        <label key={c} data-testid={`rs-stat-cov-${c}`} className={`flex cursor-pointer items-center gap-1.5 rounded-full border px-3 py-1 text-xs transition ${coxCovs.includes(c) ? "border-[#0E7C7B] bg-[#0E7C7B]/10 text-navy" : "border-slate-200 text-slate-400"}`}>
                          <input type="checkbox" checked={coxCovs.includes(c)} onChange={() => toggleCov(c)} className="accent-[#0E7C7B]" />{c}
                        </label>
                      ))}
                    </div>
                  </div>
                </>)}
                {isPaired && (
                  <div className="w-full">
                    <label className="text-xs text-slate-500">Colonnes appariées (mesures du même sujet) — {testMode === "paired2" ? "sélectionnez exactement 2" : "sélectionnez au moins 3"}</label>
                    <div className="mt-2 flex flex-wrap gap-2" data-testid="rs-stat-pair">
                      {numCols.map((c) => (
                        <label key={c} data-testid={`rs-stat-pair-${c}`} className={`flex cursor-pointer items-center gap-1.5 rounded-full border px-3 py-1 text-xs transition ${pairCols.includes(c) ? "border-[#0E7C7B] bg-[#0E7C7B]/10 text-navy" : "border-slate-200 text-slate-400"}`}>
                          <input type="checkbox" checked={pairCols.includes(c)} onChange={() => togglePair(c)} className="accent-[#0E7C7B]" />{c}
                        </label>
                      ))}
                    </div>
                    {numCols.length < (testMode === "paired2" ? 2 : 3) && <p className="mt-1 text-xs text-amber-600">Pas assez de colonnes numériques pour ce test.</p>}
                  </div>
                )}
                {(testMode === "num*" || testMode === "auto") && (
                  <p className="text-xs text-slate-500">{testMode === "num*" ? "La régression linéaire utilise automatiquement les variables numériques du jeu de données (la 1ʳᵉ comme variable à expliquer)." : "La régression logistique détecte automatiquement une variable binaire à expliquer et les prédicteurs numériques."}</p>
                )}
              </div>

              {test && (
                <p className="mt-3 text-xs text-slate-500" data-testid="rs-stat-test-desc"><b className="text-navy">Test sélectionné :</b> {TEST_LABEL[test]}</p>
              )}
              <Button onClick={launch} disabled={running || !manualReady} data-testid="rs-stat-run" className="mt-3 rounded-full bg-navy text-sm text-white hover:bg-navy/90 disabled:opacity-50">
                {running ? <Loader2 size={15} className="mr-1 animate-spin" /> : <Calculator size={15} className="mr-1" />} Lancer l'analyse
              </Button>
              {!manualReady && !running && <p className="mt-1 text-xs text-amber-600" data-testid="rs-stat-manual-hint">Sélectionnez un test et les variables requises (les 2 variables doivent être différentes).</p>}
            </div>
          )}
          {running && job && (
            <div className="mt-4" data-testid="rs-stat-progress">
              <div className="mb-1 flex justify-between text-xs text-slate-500"><span>{job.step}</span><span>{job.progress}%</span></div>
              <div className="h-2 w-full rounded bg-slate-100"><div className="h-2 rounded bg-[#0E7C7B] transition-all" style={{ width: `${job.progress || 0}%` }} /></div>
            </div>
          )}
        </div>
      )}

      {result && <StatResult result={result} exportDoc={exportDoc} exp={exp} />}
    </div>
  );
}

function StatResult({ result, exportDoc, exp }) {
  const dn = result.descriptive.numeric, dc = result.descriptive.categorical;
  const conf = Math.round(result.meta.confidence * 100);
  const [aiStatus, setAiStatus] = useState("idle");
  const [ai, setAi] = useState(null);
  const aiPoll = useRef(null);
  useEffect(() => () => aiPoll.current && clearInterval(aiPoll.current), []);
  useEffect(() => { setAiStatus("idle"); setAi(null); }, [result?._jid]);

  const runAi = async () => {
    if (!result?._jid) return;
    setAiStatus("running"); setAi(null);
    try {
      await api.post("/recherche/stats/ai-analysis", { job_id: result._jid });
      aiPoll.current = setInterval(async () => {
        try {
          const { data } = await api.get(`/recherche/stats/ai-analysis/${result._jid}`);
          if (data.status === "done") { clearInterval(aiPoll.current); setAi(data.data); setAiStatus("done"); }
          else if (data.status === "error") { clearInterval(aiPoll.current); toast.error(data.error || "Analyse IA échouée"); setAiStatus("idle"); }
        } catch { clearInterval(aiPoll.current); setAiStatus("idle"); }
      }, 3000);
    } catch (err) { toast.error(apiError(err, "Analyse IA indisponible")); setAiStatus("idle"); }
  };

  return (
    <div className="space-y-5" data-testid="rs-stat-result">
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-[#0E7C7B]/30 bg-[#0E7C7B]/5 p-4">
        <div className="flex items-center gap-3">
          {result.data_quality?.score != null && (() => {
            const sc = result.data_quality.score;
            const cls = sc >= 85 ? "border-emerald-300 bg-emerald-50 text-emerald-700" : sc >= 60 ? "border-amber-300 bg-amber-50 text-amber-700" : "border-red-300 bg-red-50 text-red-700";
            return <span data-testid="rs-stat-score" className={`flex flex-col items-center rounded-xl border px-3 py-1.5 ${cls}`}>
              <span className="text-lg font-bold leading-none">{sc}%</span>
              <span className="text-[9px] font-medium uppercase tracking-wide">Qualité</span>
            </span>;
          })()}
          <p className="text-sm font-semibold text-navy">Analyse terminée — {result.meta.n_rows} observations, {dn.length + dc.length} variables · confiance {conf}%</p>
        </div>
      </div>

      {dn.length > 0 && (
        <div className="rounded-2xl border border-slate-200 bg-white p-5">
          <h3 className="mb-3 font-semibold text-navy">Analyse descriptive — variables quantitatives</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs" data-testid="rs-stat-desc-table">
              <thead><tr className="border-b border-slate-200 text-slate-500">
                {["Variable", "n", "Moyenne", "IC moyenne", "Médiane", "É-Type", "CV%", "Étendue", "Normalité"].map((h) => <th key={h} className="px-2 py-1.5">{h}</th>)}
              </tr></thead>
              <tbody>
                {dn.map((x) => (
                  <tr key={x.name} className="border-b border-slate-100">
                    <td className="px-2 py-1.5 font-medium text-navy">{x.name}</td>
                    <td className="px-2 py-1.5">{x.count}</td>
                    <td className="px-2 py-1.5">{x.mean.toFixed(2)}</td>
                    <td className="px-2 py-1.5">[{x.ci_mean[0].toFixed(1)} ; {x.ci_mean[1].toFixed(1)}]</td>
                    <td className="px-2 py-1.5">{x.median.toFixed(2)}</td>
                    <td className="px-2 py-1.5">{x.std.toFixed(2)}</td>
                    <td className="px-2 py-1.5">{x.cv != null ? x.cv.toFixed(1) : "—"}</td>
                    <td className="px-2 py-1.5">{x.range.toFixed(1)}</td>
                    <td className="px-2 py-1.5">{x.normality.normal ? <span className="text-emerald-600">Normale</span> : <span className="text-amber-600">Non normale</span>} (p={x.normality.p != null ? x.normality.p.toFixed(3) : "—"})</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {dc.length > 0 && (
        <div className="rounded-2xl border border-slate-200 bg-white p-5">
          <h3 className="mb-3 font-semibold text-navy">Variables qualitatives</h3>
          <div className="space-y-2">
            {dc.map((x) => (
              <p key={x.name} className="text-sm text-slate-600"><b className="text-navy">{x.name}</b> (n={x.n}, {x.unique} modalités) — {x.categories.slice(0, 6).map((c) => `${c.value} ${c.pct}%`).join(", ")}. Dominante « {x.top} » {x.top_pct}% (IC [{x.ci_top_prop[0].toFixed(1)}% ; {x.ci_top_prop[1].toFixed(1)}%]).</p>
            ))}
          </div>
        </div>
      )}

      {result.trends?.length > 0 && (
        <div className="rounded-2xl border border-slate-200 bg-white p-5">
          <h3 className="mb-3 font-semibold text-navy">Tendances évolutives</h3>
          {result.trends.map((t, i) => (
            <p key={i} className="text-sm text-slate-600"><b className="text-navy">{t.y}</b> selon {t.x} : pente {t.slope.toFixed(4)} ({t.slope > 0 ? "croissante" : "décroissante"}), R²={t.r2.toFixed(3)}, p={t.p.toFixed(4)}</p>
          ))}
        </div>
      )}

      <div className="rounded-2xl border border-slate-200 bg-white p-5">
        <h3 className="mb-3 font-semibold text-navy">Analyse inférentielle</h3>
        {result.inferential.length === 0 ? <p className="text-sm text-slate-400">Aucun test applicable aux données fournies.</p> : (
          <div className="grid gap-3 md:grid-cols-2">
            {result.inferential.map((r, i) => (
              <div key={i} className="rounded-xl border border-slate-100 bg-slate-50/60 p-3" data-testid={`rs-stat-infer-${i}`}>
                <div className="flex items-center justify-between gap-2">
                  <p className="text-sm font-semibold text-navy">{r.title}</p>
                  {r.decision && r.decision !== "—" && <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${r.decision === "significatif" ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-600"}`}>{r.decision}</span>}
                </div>
                <p className="mt-1 text-xs text-slate-500">{r.stat_name} = {r.statistic != null ? r.statistic.toFixed(3) : "—"}{r.p != null ? ` · p = ${r.p.toFixed(4)}` : ""}</p>
                <p className="mt-1 text-xs text-slate-600">{r.commentaire}</p>
                {r.coefs?.length > 0 && (
                  <table className="mt-2 w-full text-left text-[11px]" data-testid={`rs-stat-cox-${i}`}>
                    <thead><tr className="text-slate-400">{["Covariable", "HR", "IC 95 %", "p"].map((h) => <th key={h} className="py-0.5 pr-2">{h}</th>)}</tr></thead>
                    <tbody>
                      {r.coefs.map((c, k) => (
                        <tr key={k} className="border-t border-slate-100">
                          <td className="py-0.5 pr-2 font-medium text-navy">{c.var}</td>
                          <td className="py-0.5 pr-2">{c.hr.toFixed(2)}</td>
                          <td className="py-0.5 pr-2">[{c.ci_low.toFixed(2)} ; {c.ci_high.toFixed(2)}]</td>
                          <td className="py-0.5 pr-2">{c.p < 0.05 ? <span className="font-semibold text-emerald-600">{c.p.toFixed(3)}</span> : <span className="text-slate-500">{c.p.toFixed(3)}</span>}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
                {r.medians?.length > 0 && (
                  <table className="mt-2 w-full text-left text-[11px]" data-testid={`rs-stat-medians-${i}`}>
                    <thead><tr className="text-slate-400">{["Groupe", "n", "Évén.", "Médiane survie"].map((h) => <th key={h} className="py-0.5 pr-2">{h}</th>)}</tr></thead>
                    <tbody>
                      {r.medians.map((m, k) => (
                        <tr key={k} className="border-t border-slate-100">
                          <td className="py-0.5 pr-2 font-medium text-navy">{m.group}</td><td className="py-0.5 pr-2">{m.n}</td><td className="py-0.5 pr-2">{m.events}</td>
                          <td className="py-0.5 pr-2">{m.median != null ? m.median.toFixed(1) : "non atteinte"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
                {r.posthoc?.length > 0 && (
                  <div className="mt-2" data-testid={`rs-stat-posthoc-${i}`}>
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">Post-hoc — {r.posthoc_name}</p>
                    <table className="mt-1 w-full text-left text-[11px]">
                      <thead><tr className="text-slate-400">{["Comparaison", "p ajusté", ""].map((h, k) => <th key={k} className="py-0.5 pr-2">{h}</th>)}</tr></thead>
                      <tbody>
                        {r.posthoc.map((pp, k) => (
                          <tr key={k} className="border-t border-slate-100">
                            <td className="py-0.5 pr-2 text-navy">{pp.a} vs {pp.b}</td>
                            <td className="py-0.5 pr-2">{pp.p != null ? pp.p.toFixed(4) : "—"}</td>
                            <td className="py-0.5 pr-2">{pp.significant ? <span className="font-semibold text-emerald-600">significatif</span> : <span className="text-slate-400">ns</span>}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {result.charts?.length > 0 && (
        <div className="rounded-2xl border border-slate-200 bg-white p-5">
          <h3 className="mb-3 font-semibold text-navy">Graphiques</h3>
          <div className="grid gap-4 sm:grid-cols-2">
            {result.charts.map((c) => (
              <figure key={c.id} className="rounded-xl border border-slate-100 p-2" data-testid={`rs-stat-chart-${c.id}`}>
                <img src={`data:image/png;base64,${c.png_b64}`} alt={c.title} className="w-full rounded" />
              </figure>
            ))}
          </div>
        </div>
      )}

      <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="rs-stat-discussion">
        <h3 className="mb-3 font-semibold text-navy">Discussion (littérature internationale)</h3>
        {(result.discussion || "").split("\n").filter((p) => p.trim()).map((p, i) => <p key={i} className="mb-2 text-sm leading-relaxed text-slate-600">{p}</p>)}
      </div>

      {result.data_quality && (
        <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="rs-stat-quality">
          <h3 className="mb-3 font-semibold text-navy">Qualité des données</h3>
          {result.data_quality.score != null && (
            <p className="mb-2 text-sm text-slate-600">Score global : <b className="text-navy">{result.data_quality.score}%</b> — complétude {result.data_quality.completeness}%, plausibilité {result.data_quality.plausibility}%.</p>
          )}
          <p className="text-sm text-slate-600">Observations analysées : <b className="text-navy">{result.data_quality.n_final}</b> (initial : {result.data_quality.n_original}{result.data_quality.rows_removed ? `, ${result.data_quality.rows_removed} ligne(s) retirée(s)` : ""}). Traitement des manquantes : {result.data_quality.missing_strategy}.</p>
          <p className="mt-1 text-sm text-slate-600">Valeurs aberrantes neutralisées : <b className="text-navy">{result.data_quality.anomalies_fixed}</b>.{result.data_quality.excluded_columns?.length ? ` Variables exclues : ${result.data_quality.excluded_columns.join(", ")}.` : ""}</p>
          {result.data_quality.missing_by_col?.length > 0 && (
            <p className="mt-1 text-sm text-slate-600">Manquantes résiduelles : {result.data_quality.missing_by_col.map((m) => `${m.name} (${m.missing} · ${m.pct}%)`).join(", ")}.</p>
          )}
          {!result.data_quality.missing_by_col?.length && !result.data_quality.anomalies_fixed && !result.data_quality.rows_removed && (
            <p className="mt-1 text-sm text-emerald-600">Aucune valeur manquante ni aberrante : données de bonne qualité.</p>
          )}
        </div>
      )}

      {(aiStatus === "running" || ai) && (
        <div className="rounded-2xl border border-[#0E7C7B]/30 bg-white p-5" data-testid="rs-stat-ai">
          <h3 className="mb-3 flex items-center gap-2 font-semibold text-navy"><Sparkles size={16} className="text-[#0E7C7B]" /> Analyse IA structurée</h3>
          {aiStatus === "running" && <p className="flex items-center gap-2 text-sm text-slate-500" data-testid="rs-stat-ai-loading"><Loader2 size={15} className="animate-spin text-[#0E7C7B]" /> L'IA analyse les indicateurs et compare à la littérature (africaine puis universelle)…</p>}
          {ai && (
            <div className="space-y-4">
              <div data-testid="rs-stat-ai-v1">
                <p className="text-xs font-semibold uppercase tracking-wide text-[#0E7C7B]">Volet 1 — Description des indicateurs</p>
                {ai.volet1?.synthese && <p className="mt-1 text-sm leading-relaxed text-slate-600">{ai.volet1.synthese}</p>}
                {ai.volet1?.indicateurs?.length > 0 && (
                  <div className="mt-2 overflow-x-auto">
                    <table className="w-full text-left text-xs">
                      <thead><tr className="border-b border-slate-200 text-slate-500">{["Indicateur", "Valeur", "Lecture"].map((h) => <th key={h} className="px-2 py-1.5">{h}</th>)}</tr></thead>
                      <tbody>
                        {ai.volet1.indicateurs.map((it, k) => (
                          <tr key={k} className="border-b border-slate-100 align-top">
                            <td className="px-2 py-1.5 font-medium text-navy">{it.indicateur}</td>
                            <td className="px-2 py-1.5 text-[#0E7C7B]">{it.valeur}</td>
                            <td className="px-2 py-1.5 text-slate-600">{it.lecture}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
              <div data-testid="rs-stat-ai-v2" className="space-y-2">
                <p className="text-xs font-semibold uppercase tracking-wide text-[#0E7C7B]">Volet 2 — Interprétation & benchmarking</p>
                {ai.volet2?.interpretation && <p className="text-sm leading-relaxed text-slate-600"><b className="text-navy">Interprétation. </b>{ai.volet2.interpretation}</p>}
                {ai.volet2?.benchmarking && <p className="text-sm leading-relaxed text-slate-600"><b className="text-navy">Benchmarking (littérature africaine puis universelle). </b>{ai.volet2.benchmarking}</p>}
                {ai.volet2?.limites && <p className="text-sm leading-relaxed text-slate-500"><b className="text-navy">Limites. </b>{ai.volet2.limites}</p>}
              </div>
            </div>
          )}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-3 rounded-2xl border border-slate-200 bg-white p-4" data-testid="rs-stat-actions">
        <Button onClick={runAi} disabled={aiStatus === "running"} data-testid="rs-stat-ai-run" className="rounded-full bg-navy text-sm text-white hover:bg-navy-light">
          {aiStatus === "running" ? <Loader2 size={15} className="mr-1 animate-spin" /> : <Sparkles size={15} className="mr-1" />} Analyse IA
        </Button>
        <div className="h-6 w-px bg-slate-200" />
        <span className="text-xs font-medium text-slate-400">Télécharger :</span>
        <div className="flex flex-wrap gap-2">
          <Button onClick={() => exportDoc("docx")} disabled={!!exp} data-testid="rs-stat-export-docx" variant="outline" className="rounded-full border-[#0E7C7B] text-xs text-[#0E7C7B]">{exp === "docx" ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Download size={14} className="mr-1" />} Word</Button>
          <Button onClick={() => exportDoc("pdf")} disabled={!!exp} data-testid="rs-stat-export-pdf" variant="outline" className="rounded-full border-[#0E7C7B] text-xs text-[#0E7C7B]">{exp === "pdf" ? <Loader2 size={14} className="mr-1 animate-spin" /> : <FileDown size={14} className="mr-1" />} PDF</Button>
          <Button onClick={() => exportDoc("xlsx")} disabled={!!exp} data-testid="rs-stat-export-xlsx" variant="outline" className="rounded-full border-[#0E7C7B] text-xs text-[#0E7C7B]">{exp === "xlsx" ? <Loader2 size={14} className="mr-1 animate-spin" /> : <FileDown size={14} className="mr-1" />} Excel</Button>
        </div>
      </div>
    </div>
  );
}

function PlagiatBadge({ p }) {
  const pl = p.plagiat || {};
  if (pl.status === "pending") return <span className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-slate-50 px-2.5 py-0.5 text-[11px] font-medium text-slate-500" data-testid={`rs-plagiat-${p.id}`}><Loader2 size={11} className="animate-spin" /> Intégrité en cours…</span>;
  if (pl.status === "checked") {
    const ok = pl.verdict === "conforme";
    return <span className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-[11px] font-semibold ${ok ? "border-emerald-300 bg-emerald-50 text-emerald-700" : "border-amber-300 bg-amber-50 text-amber-700"}`} data-testid={`rs-plagiat-${p.id}`}>
      {ok ? <CheckCircle2 size={11} /> : <AlertTriangle size={11} />} Plagiat {pl.score != null ? `${pl.score}%` : "—"} · {ok ? "conforme" : "à revoir"}
    </span>;
  }
  if (pl.status === "error") return <span className="rounded-full border border-red-200 bg-red-50 px-2.5 py-0.5 text-[11px] font-medium text-red-600" data-testid={`rs-plagiat-${p.id}`}>Contrôle échoué</span>;
  return <span className="rounded-full border border-slate-200 bg-slate-50 px-2.5 py-0.5 text-[11px] font-medium text-slate-400" data-testid={`rs-plagiat-${p.id}`}>Intégrité non vérifiée</span>;
}

function Validation() {
  const [papers, setPapers] = useState(null);
  const [status, setStatus] = useState("pending");
  const [dl, setDl] = useState("");
  const load = (st) => api.get(`/admin/recherche/papers?status=${st}`).then(({ data }) => setPapers(data.papers)).catch(() => setPapers([]));
  useEffect(() => { setPapers(null); load(status); const t = status === "pending" ? setInterval(() => load(status), 8000) : null; return () => t && clearInterval(t); }, [status]);
  const review = async (id, action) => {
    try { await api.post(`/admin/recherche/papers/${id}/review`, { action, comment: "" }); toast.success(action === "approve" ? "Publication approuvée" : "Publication rejetée"); load(status); }
    catch (e) { toast.error(apiError(e)); }
  };
  const downloadCert = async (id) => {
    setDl(id);
    try {
      const res = await api.get(`/recherche/papers/${id}/certificate`, { responseType: "blob" });
      blobDownload(res, "certificat-validation.pdf");
    } catch (e) { toast.error(apiError(e, "Certificat indisponible")); } finally { setDl(""); }
  };
  const downloadReport = async (id) => {
    setDl(`${id}-rep`);
    try {
      const res = await api.get(`/recherche/papers/${id}/plagiat-report`, { responseType: "blob" });
      blobDownload(res, "rapport-plagiat.pdf");
    } catch (e) { toast.error(apiError(e, "Rapport indisponible")); } finally { setDl(""); }
  };
  const recheck = async (id) => {
    setDl(`${id}-rc`);
    try { await api.post(`/admin/recherche/papers/${id}/plagiat-recheck`); toast.success("Analyse d'intégrité relancée"); load(status); }
    catch (e) { toast.error(apiError(e, "Relance impossible")); } finally { setDl(""); }
  };
  return (
    <div data-testid="rs-validation">
      <div className="mb-4 inline-flex rounded-full border border-slate-200 bg-slate-50 p-1 text-sm">
        {[["pending", "En attente"], ["approved", "Validés"]].map(([k, lbl]) => (
          <button key={k} onClick={() => setStatus(k)} data-testid={`rs-val-tab-${k}`}
            className={`rounded-full px-4 py-1.5 font-medium transition ${status === k ? "bg-[#0E7C7B] text-white" : "text-slate-500 hover:text-navy"}`}>{lbl}</button>
        ))}
      </div>
      {!papers ? <div className="flex justify-center py-12"><Loader2 className="animate-spin text-[#0E7C7B]" /></div>
        : papers.length === 0 ? <p className="py-12 text-center text-sm text-slate-400" data-testid="rs-val-empty">{status === "pending" ? "Aucune publication en attente." : "Aucune publication validée."}</p>
        : (
          <div className="space-y-3">
            {papers.map((p) => {
              const pst = (p.plagiat || {}).status;
              return (
              <div key={p.id} className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-slate-200 bg-white p-4" data-testid={`rs-val-${p.id}`}>
                <div className="min-w-0">
                  <p className="font-semibold text-navy">{p.title}</p>
                  <p className="text-xs text-slate-500">{p.authors} · {p.theme}{p.year ? ` · ${p.year}` : ""} · soumis par {p.submitter_name || "?"} le {fmtDate(p.created_at)}</p>
                  <div className="mt-2"><PlagiatBadge p={p} /></div>
                </div>
                <div className="flex flex-wrap gap-2">
                  {pst === "checked" && (
                    <Button onClick={() => downloadReport(p.id)} disabled={dl === `${p.id}-rep`} data-testid={`rs-report-${p.id}`} variant="outline" className="rounded-full border-slate-300 text-xs text-slate-600">{dl === `${p.id}-rep` ? <Loader2 size={14} className="mr-1 animate-spin" /> : <FileDown size={14} className="mr-1" />} Rapport détaillé</Button>
                  )}
                  {(!pst || pst === "unavailable" || pst === "error") && (
                    <Button onClick={() => recheck(p.id)} disabled={dl === `${p.id}-rc`} data-testid={`rs-recheck-${p.id}`} variant="outline" className="rounded-full border-amber-300 text-xs text-amber-700">{dl === `${p.id}-rc` ? <Loader2 size={14} className="mr-1 animate-spin" /> : <RefreshCw size={14} className="mr-1" />} Relancer l'analyse</Button>
                  )}
                  {status === "pending" ? (
                    <>
                      <Button onClick={() => review(p.id, "approve")} data-testid={`rs-approve-${p.id}`} className="rounded-full bg-emerald-600 text-xs text-white hover:bg-emerald-700"><CheckCircle2 size={14} className="mr-1" /> Approuver</Button>
                      <Button onClick={() => review(p.id, "reject")} data-testid={`rs-reject-${p.id}`} variant="outline" className="rounded-full border-red-300 text-xs text-red-600"><XCircle size={14} className="mr-1" /> Rejeter</Button>
                    </>
                  ) : (
                    <Button onClick={() => downloadCert(p.id)} disabled={dl === p.id} data-testid={`rs-cert-${p.id}`} variant="outline" className="rounded-full border-[#0E7C7B] text-xs text-[#0E7C7B]">{dl === p.id ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Award size={14} className="mr-1" />} Certificat de validation</Button>
                  )}
                </div>
              </div>
              );
            })}
          </div>
        )}
    </div>
  );
}
